/**
 * Game Store
 * 
 * Main state management for the blackjack trainer application.
 * 
 * This store manages:
 * - Training mode gameplay (hit, stand, double, split, etc.)
 * - Table simulator state
 * - Speed drill state
 * - Statistics and achievements
 * - Game settings
 * 
 * @see ./gameStore/types.ts for type definitions
 * @see ./gameStore/defaults.ts for default configurations
 * @see ./gameStore/helpers.ts for utility functions
 */

import { create } from 'zustand';
import { Card, GameSettings, Statistics, BetAnalysisResult, Hand, HandHistoryEntry, AchievementProgress, DealingPhase } from '../types';
import { createShoe, createSeededShoe, getHandValue, isBlackjack, calculateTrueCount, getDecksRemaining, shouldReshuffle } from '../utils/deck';
import { getCardValue } from '../utils/countingSystems';
import { getBasicStrategyDecision } from '../utils/basicStrategy';
import { analyzeDecision, analyzeInsuranceDecision } from '../utils/analysis';
import { getOptimalBet, evaluateBetSizing } from '../utils/betting';
import * as sounds from '../utils/sounds';
import { DEFAULT_ACHIEVEMENTS, getAchievementProgressByDefinition } from '../utils/achievements';
import { queueStatsSave, queueAchievementsSave, queueSettingsSave, queueHighScoresSave, saveLocalSession, saveLocalHistory } from '../services/userState';

// Import from modular files
import type { GameStore } from './gameStore/types';
import { 
  DEFAULT_SETTINGS, 
  DEFAULT_STATISTICS, 
  DEFAULT_TABLE_CONFIG, 
  DEFAULT_COUNT_CHECK,
  XP_REWARDS,
  buildSpeedDrillDefaults,
  createSeats
} from './gameStore/defaults';
import {
  calculateLevel,
  combineAchievementDefinitions,
  computeAchievementReward,
  makeCard,
  stackDeck,
  buildTableStack,
  persistStats,
  updateStatsWithBankroll,
  loadTrainingSession,
  loadTableSession,
  revealDealerHoleAndMaybeResolveBJ,
  restoreDebugSettingsIfNeeded
} from './gameStore/helpers';

// Re-export types for convenience
export type { GameStore } from './gameStore/types';

// Mutable flag for settings persistence suppression during debug
let suppressSettingsPersist = false;

/**
 * Safe card draw helper.
 * Pops one card from the provided deck array (mutating it in place by returning a new array).
 * If the deck is empty, creates a fresh shoe from numDecks and draws from that instead.
 * Returns { card, deck } where deck is the updated (shorter) array.
 * The caller is responsible for resetting running/true count when a reshuffle occurred
 * (reshuffled is true in that case).
 */
function drawCard(deck: Card[], numDecks: number): {
  card: Card;
  deck: Card[];
  reshuffled: boolean;
} {
  let d = deck;
  let reshuffled = false;
  if (d.length === 0) {
    // Mid-round exhaustion: build a fresh shoe and reset count.
    d = createShoe(numDecks);
    reshuffled = true;
  }
  const card = d[d.length - 1];
  return { card, deck: d.slice(0, -1), reshuffled };
}

// Note: GameStore interface, defaults, and helpers are now imported from modular files
// - Types: ./gameStore/types.ts
// - Defaults: ./gameStore/defaults.ts  
// - Helpers: ./gameStore/helpers.ts

// Load persisted data
// Initial defaults (Firebase data is hydrated on login via hydrateStoresFromUserState)
const loadedSettings: GameSettings = { ...DEFAULT_SETTINGS };
const loadedStats: Statistics = { ...DEFAULT_STATISTICS };
const loadedHistory: HandHistoryEntry[] = [];
const loadedHighScore = 0;
const loadedAchievements: AchievementProgress[] = [];
const loadedBankroll = DEFAULT_SETTINGS.startingBankroll;

// Cache showTutorial in localStorage for instant load (avoids flash before Firebase hydrates)
const TUTORIAL_CACHE_KEY = 'blackjack-trainer-tutorial-dismissed';
const cachedTutorialDismissed = (() => {
  try {
    return localStorage.getItem(TUTORIAL_CACHE_KEY) === 'true';
  } catch {
    return false;
  }
})();
const hasSeenTutorial = cachedTutorialDismissed;
const initialMinBet = DEFAULT_SETTINGS.minBet;
const initialTrainingSession = loadTrainingSession(loadedSettings, initialMinBet);
const initialTableSession = loadTableSession(loadedSettings);

// Sync sound setting on load
if (loadedSettings.soundEnabled !== undefined) {
  sounds.setSoundEnabled(loadedSettings.soundEnabled);
}

export const useGameStore = create<GameStore>((set, get) => ({
  settings: { ...DEFAULT_SETTINGS, ...loadedSettings },
  statistics: { ...DEFAULT_STATISTICS, ...loadedStats, currentBankroll: loadedBankroll ?? loadedStats.currentBankroll ?? DEFAULT_SETTINGS.startingBankroll },
  gameMode: 'training',
  speedDrill: buildSpeedDrillDefaults({ ...DEFAULT_SETTINGS, ...loadedSettings }, loadedHighScore),
  tableConfig: DEFAULT_TABLE_CONFIG,
  seats: initialTableSession.seats,
  tableState: initialTableSession.tableState,
  countCheck: DEFAULT_COUNT_CHECK,
  handHistory: loadedHistory,
  achievements: loadedAchievements,
  achievementDefinitions: [...DEFAULT_ACHIEVEMENTS, ...(loadedSettings.customAchievements || [])],
  showTutorial: !hasSeenTutorial,

  // Training mode deck state
  deck: initialTrainingSession.deck,
  dealtCards: initialTrainingSession.dealtCards,
  deckSeed: initialTrainingSession.deckSeed,
  playerHand: initialTrainingSession.playerHands[0] ?? { cards: [], bet: 0 },
  playerHands: initialTrainingSession.playerHands,
  currentHandIndex: initialTrainingSession.currentHandIndex,
  dealerHand: initialTrainingSession.dealerHand,
  runningCount: initialTrainingSession.runningCount,
  trueCount: initialTrainingSession.trueCount,
  userInputCount: undefined,
  isPlaying: initialTrainingSession.isPlaying,
  isDealing: initialTrainingSession.isDealing,
  playerTurnComplete: initialTrainingSession.playerTurnComplete,
  roundComplete: initialTrainingSession.roundComplete,
  bankroll: loadedBankroll ?? DEFAULT_SETTINGS.startingBankroll,
  lastBaseBet: initialTrainingSession.lastBaseBet ?? initialMinBet,
  lastRoundNet: initialTrainingSession.lastRoundNet ?? 0,
  autoDeal: initialTrainingSession.autoDeal ?? false,
  dealSpeed: initialTrainingSession.dealSpeed ?? 1000,
  insuranceBet: initialTrainingSession.insuranceBet ?? 0,
  insuranceOffered: initialTrainingSession.insuranceOffered ?? false,
  insuranceResult: initialTrainingSession.insuranceResult ?? null,
  lastInsuranceAnalysis: initialTrainingSession.lastInsuranceAnalysis ?? null,

  // Separate deck state for table simulator
  tableSimDeck: initialTableSession.tableSimDeck,

  // Separate deck state for speed drill
  speedDrillDeck: (() => {
    const { deck, seed } = createSeededShoe(DEFAULT_SETTINGS.numDecks);
    return {
      deck,
      dealtCards: [],
      deckSeed: seed,
      runningCount: 0
    };
  })(),

  updateSettings: (newSettings) => {
    const state = get();
    const settings = { ...state.settings, ...newSettings };
    const achievementDefinitions = combineAchievementDefinitions(settings);

    // Sync sound setting
    if (newSettings.soundEnabled !== undefined) {
      sounds.setSoundEnabled(newSettings.soundEnabled);
    }

    // Note: Settings are persisted via queueSettingsSave in subscriber

    // If deck count or system changed, create new shoe
    const shouldUpdateSpeedDrillDefaults =
      (newSettings.defaultSpeedDrillDuration !== undefined || newSettings.defaultSpeedDrillSpeed !== undefined) &&
      !state.speedDrill.isActive;

    if (newSettings.numDecks !== undefined || newSettings.countingSystem !== undefined) {
      const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
      const statistics =
        newSettings.leveling !== undefined
          ? { ...state.statistics, level: calculateLevel(state.statistics.totalXP, settings.leveling) }
          : state.statistics;
      set({
        settings,
        achievementDefinitions,
        deck: newDeck,
        deckSeed: newSeed,
        runningCount: 0,
        trueCount: 0,
        dealtCards: [],
        statistics,
        speedDrill: shouldUpdateSpeedDrillDefaults ? buildSpeedDrillDefaults(settings, state.speedDrill.highScore) : state.speedDrill
      });
    } else {
      const statistics =
        newSettings.leveling !== undefined
          ? { ...state.statistics, level: calculateLevel(state.statistics.totalXP, settings.leveling) }
          : state.statistics;
      set({
        settings,
        achievementDefinitions,
        statistics,
        speedDrill: shouldUpdateSpeedDrillDefaults ? buildSpeedDrillDefaults(settings, state.speedDrill.highScore) : state.speedDrill
      });
    }
  },

  setGameMode: (mode) => {
    const state = get();

    // Pause speed drill when switching modes (but don't reset it)
    if (state.speedDrill.isActive) {
      set({
        speedDrill: {
          ...state.speedDrill,
          isPaused: true
        },
        autoDeal: false
      });
    }

    // Pause auto-deal when switching away from table-simulator
    // This preserves the table state so user can come back
    if (state.gameMode === 'table-simulator' && mode !== 'table-simulator') {
      set({ autoDeal: false });
    }

    // Don't reset training or table sim state when switching modes
    // This allows users to switch tabs and come back to see their last hand

    set({ gameMode: mode });
  },

  reshuffleShoe: () => {
    const { settings, speedDrill } = get();
    const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
    sounds.playShuffle();
    set({
      deck: newDeck,
      deckSeed: newSeed,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      playerHand: { cards: [], bet: 0 },
      playerHands: [{ cards: [], bet: 0 }],
      currentHandIndex: 0,
      dealerHand: { cards: [], bet: 0 },
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false,
      insuranceBet: 0,
      insuranceOffered: false,
      insuranceResult: null,
      lastInsuranceAnalysis: null,
      lastBaseBet: settings.minBet,
      // Also reset speedDrill finished state when reshuffling (exiting drill)
      // Preserve speed and duration settings
      speedDrill: {
        ...speedDrill,
        isActive: false,
        isPaused: false,
        isFinished: false
      },
      autoDeal: false
    });
  },

  tableReshuffle: () => {
    const { settings, tableConfig } = get();
    const { deck, seed } = createSeededShoe(settings.numDecks);
    sounds.playShuffle();
    const resetSeats = createSeats(tableConfig).map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck,
        deckSeed: seed,
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: resetSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      },
      autoDeal: false
    });
  },

  placeBet: (amount) => {
    const state = get();
    // Enforce minimum and maximum (allow 0 to clear)
    if (amount < state.settings.minBet && amount !== 0) return;
    if (amount > state.settings.maxBet) return;
    // Ensure there will be enough bankroll when the hand is dealt
    const existingBet = state.playerHand?.bet || 0;
    const refundable = state.isPlaying ? 0 : existingBet;
    const available = state.bankroll + refundable;
    if (amount > available) return;

    // Calculate optimal bet and evaluate the player's choice
    const optimalBet = getOptimalBet(
      state.trueCount,
      state.settings.minBet,
      state.settings.maxBet,
      state.bankroll,
      'moderate'
    );

    const evaluation = evaluateBetSizing(amount, optimalBet, state.settings.minBet);

    // Generate feedback message
    let feedback: string;
    if (evaluation.rating === 'perfect') {
      feedback = `Perfect! $${amount} is the optimal bet at TC ${state.trueCount > 0 ? '+' : ''}${state.trueCount.toFixed(1)}`;
    } else if (evaluation.rating === 'good') {
      feedback = `Good bet! Close to optimal ($${optimalBet})`;
    } else if (evaluation.rating === 'acceptable') {
      feedback = `Acceptable, but optimal is $${optimalBet} at TC ${state.trueCount > 0 ? '+' : ''}${state.trueCount.toFixed(1)}`;
    } else {
      if (amount > optimalBet) {
        feedback = `Over-betting! Should be $${optimalBet} at TC ${state.trueCount > 0 ? '+' : ''}${state.trueCount.toFixed(1)}`;
      } else {
        feedback = `Under-betting! Should be $${optimalBet} at TC ${state.trueCount > 0 ? '+' : ''}${state.trueCount.toFixed(1)}`;
      }
    }

    const betAnalysis: BetAnalysisResult = {
      isCorrect: evaluation.accurate,
      actualBet: amount,
      optimalBet,
      trueCountAtBet: state.trueCount,
      percentageOff: evaluation.percentageOff,
      rating: evaluation.rating,
      feedback
    };

    // Update statistics
    const newStats = { ...state.statistics };
    if (evaluation.rating === 'perfect') {
      newStats.perfectBets = (newStats.perfectBets || 0) + 1;
      newStats.totalXP += XP_REWARDS.perfectBet;
      newStats.level = calculateLevel(newStats.totalXP, state.settings.leveling);
    }
    // NOTE: betSizingAccuracy is recorded once per dealt hand in dealNewHand (weighted by
    // betsPlaced), not here — placeBet can fire many times per hand as the user adjusts chips.

    // Update both single-hand and multi-hand representations
    const updatedPlayerHands = state.playerHands.map((hand, idx) => idx === 0 ? { ...hand, bet: amount } : hand);

    set({
      playerHand: { ...state.playerHand, bet: amount },
      playerHands: updatedPlayerHands,
      lastBaseBet: amount > 0 ? amount : state.settings.minBet,
      lastBetAnalysis: betAnalysis,
      statistics: newStats
    });
  },

  dealNewHand: () => {
    const state = get();
    const { deck, settings, dealtCards, runningCount, trueCount, bankroll, lastBaseBet } = state;

    // Check if reshuffle needed
    const totalCards = settings.numDecks * 52;
    if (shouldReshuffle(deck, totalCards, settings.penetration) || settings.shuffleAfterHand) {
      state.reshuffleShoe();
      return;
    }

    if (deck.length < 10) {
      state.reshuffleShoe();
      return;
    }

    // Get the current bet amount (use saved base bet to avoid keeping doubles)
    const baseBet = (lastBaseBet ?? state.playerHand.bet) || settings.minBet;
    const currentBet = baseBet > 0 ? baseBet : settings.minBet;
    if (currentBet < settings.minBet || currentBet > settings.maxBet) return;
    if (bankroll < currentBet) return;

    // Re-evaluate the bet at the CURRENT true count (before dealing)
    const optimalBet = getOptimalBet(
      trueCount,
      settings.minBet,
      settings.maxBet,
      bankroll,
      'moderate'
    );

    const evaluation = evaluateBetSizing(currentBet, optimalBet, settings.minBet);

    // Generate feedback message
    let feedback: string;
    if (evaluation.rating === 'perfect') {
      feedback = `Perfect! $${currentBet} is the optimal bet at TC ${trueCount > 0 ? '+' : ''}${trueCount.toFixed(1)}`;
    } else if (evaluation.rating === 'good') {
      feedback = `Good bet! Close to optimal ($${optimalBet})`;
    } else if (evaluation.rating === 'acceptable') {
      feedback = `Acceptable, but optimal is $${optimalBet} at TC ${trueCount > 0 ? '+' : ''}${trueCount.toFixed(1)}`;
    } else {
      if (currentBet > optimalBet) {
        feedback = `Over-betting! Should be $${optimalBet} at TC ${trueCount > 0 ? '+' : ''}${trueCount.toFixed(1)}`;
      } else {
        feedback = `Under-betting! Should be $${optimalBet} at TC ${trueCount > 0 ? '+' : ''}${trueCount.toFixed(1)}`;
      }
    }

    const betAnalysis: BetAnalysisResult = {
      isCorrect: evaluation.accurate,
      actualBet: currentBet,
      optimalBet,
      trueCountAtBet: trueCount,
      percentageOff: evaluation.percentageOff,
      rating: evaluation.rating,
      feedback
    };

    // Record exactly one bet-sizing accuracy sample per dealt hand (rolling average
    // weighted by betsPlaced, so chip-by-chip placeBet adjustments don't over-count).
    const newStats = { ...state.statistics };
    const priorBets = newStats.betsPlaced || 0;
    newStats.betSizingAccuracy = Math.round(
      ((newStats.betSizingAccuracy || 0) * priorBets + (evaluation.accurate ? 100 : 0)) / (priorBets + 1)
    );
    newStats.betsPlaced = priorBets + 1;

    // Deduct bet from bankroll for this hand
    const bankrollAfterStake = bankroll - currentBet;

    // Initialize state for sequential dealing (fresh decisions log for new round)
    const initialHand: Hand = { cards: [], bet: currentBet, result: undefined, decisions: [] };
    set({
      playerHand: initialHand,
      playerHands: [initialHand],
      currentHandIndex: 0,
      dealerHand: { cards: [], bet: 0, result: undefined },
      isPlaying: true,
      isDealing: true,
      playerTurnComplete: false,
      roundComplete: false,
      lastDecisionAnalysis: null,
      lastBetAnalysis: betAnalysis,
      insuranceBet: 0,
      insuranceOffered: false,
      insuranceResult: null,
      lastInsuranceAnalysis: null,
      bankroll: bankrollAfterStake,
      lastRoundNet: 0,
      statistics: newStats
    });

    // Deal cards sequentially like real blackjack:
    // 1. Player card 1
    // 2. Dealer card 1 (face up)
    // 3. Player card 2
    // 4. Dealer card 2 (face down)
    const tempDeck = [...deck];
    const tempDealtCards = [...dealtCards];
    let tempRunningCount = runningCount;
    // Use a fast fixed delay for training mode (200ms between cards)
    const cardDelay = 200;

    const dealSequence = [
      { target: 'player', faceUp: true },
      { target: 'dealer', faceUp: true },
      { target: 'player', faceUp: true },
      { target: 'dealer', faceUp: false }
    ];

    const dealNextCard = (index: number) => {
      if (index >= dealSequence.length) {
        // All cards dealt, finalize
        const decksRemaining = getDecksRemaining(tempDeck.length);
        const newTrueCount = calculateTrueCount(tempRunningCount, decksRemaining);

        set({
          deck: tempDeck,
          dealtCards: tempDealtCards,
          runningCount: tempRunningCount,
          trueCount: newTrueCount,
          isDealing: false
        });

        // Check for immediate blackjack or insurance opportunity
        const currentState = get();
        const dealerUpCard = currentState.dealerHand.cards[0];

        // Offer insurance if dealer shows Ace and insurance is allowed
        if (dealerUpCard && dealerUpCard.rank === 'A' && currentState.settings.insuranceAllowed) {
          set({ insuranceOffered: true });
          // Don't auto-stand on blackjack yet - wait for insurance decision
          return;
        }

        // If dealer shows a 10-value and has blackjack, reveal after a short delay and end hand
        if (dealerUpCard && ['10', 'J', 'Q', 'K'].includes(dealerUpCard.rank) && isBlackjack(currentState.dealerHand.cards)) {
          // Freeze controls while revealing
          set({ isDealing: true, playerTurnComplete: true });
          revealDealerHoleAndMaybeResolveBJ(get, set);
          return;
        }

        // Check for immediate blackjack
        if (isBlackjack(currentState.playerHand.cards)) {
          // Resolve immediately on player blackjack; no dealer draw needed unless dealer also has blackjack
            get().stand();
        }
        return;
      }

      const { target, faceUp } = dealSequence[index];
      const card = tempDeck.pop()!;
      const dealtCard = { ...card, faceUp };
      tempDealtCards.push(dealtCard);

      // Play card deal sound
      sounds.playCardDeal();

      // Update running count for face-up cards
      if (faceUp) {
        tempRunningCount += getCardValue(card.rank, settings.countingSystem);
      }

      // Update the appropriate hand
      set(prevState => {
        if (target === 'player') {
          const newPlayerHand = {
            ...prevState.playerHand,
            cards: [...prevState.playerHand.cards, dealtCard]
          };
          return {
            playerHand: newPlayerHand,
            playerHands: [newPlayerHand]
          };
        } else {
          return {
            dealerHand: {
              ...prevState.dealerHand,
              cards: [...prevState.dealerHand.cards, dealtCard]
            }
          };
        }
      });

      // Persist deck/dealt state incrementally so cards dealt grows one-by-one
      const decksRemaining = getDecksRemaining(tempDeck.length);
      const incrementalTrueCount = calculateTrueCount(tempRunningCount, decksRemaining);
      set({
        deck: [...tempDeck],
        dealtCards: [...tempDealtCards],
        runningCount: tempRunningCount,
        trueCount: incrementalTrueCount,
        isDealing: true
      });

      // Deal next card after delay
      setTimeout(() => dealNextCard(index + 1), cardDelay);
    };

    // Start dealing sequence
    dealNextCard(0);
  },

  hit: () => {
    const state = get();
    if (!state.isPlaying || state.roundComplete) return;

    // Get current hand (support for split hands)
    const currentHand = state.playerHands[state.currentHandIndex];
    if (!currentHand) return;

    // Analyze the decision BEFORE hitting
    const analysis = analyzeDecision(
      currentHand.cards,
      state.dealerHand.cards[0],
      'hit',
      state.settings,
      state.trueCount
    );

    let newDeck = [...state.deck];
    const newDealtCards = [...state.dealtCards];

    const { card, deck: deckAfterHit, reshuffled: hitReshuffled } = drawCard(newDeck, state.settings.numDecks);
    newDeck = deckAfterHit;
    const dealtCard = { ...card, faceUp: true };
    newDealtCards.push(dealtCard);

    const newPlayerCards = [...currentHand.cards, dealtCard];
    const { value } = getHandValue(newPlayerCards);

    // Play hit sound
    sounds.playCardDeal();

    // Update count (reset on mid-round reshuffle)
    const newRunningCount = hitReshuffled ? getCardValue(card.rank, state.settings.countingSystem) : state.runningCount + getCardValue(card.rank, state.settings.countingSystem);
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    // Update the current hand in playerHands array (append decision log)
    const hitDecisionStr = analysis.isCorrect
      ? `hit (correct)`
      : `hit (incorrect — should ${analysis.bestAction})`;
    const newPlayerHands = [...state.playerHands];
    newPlayerHands[state.currentHandIndex] = {
      ...currentHand,
      cards: newPlayerCards,
      decisions: [...(currentHand.decisions ?? []), hitDecisionStr],
    };

    set({
      deck: newDeck,
      dealtCards: newDealtCards,
      playerHand: newPlayerHands[0],
      playerHands: newPlayerHands,
      runningCount: newRunningCount,
      trueCount: newTrueCount,
      lastDecisionAnalysis: analysis,
      // If busting on hit, stop prompting for further actions immediately
      playerTurnComplete: value >= 21 || state.playerTurnComplete
    });

    // Auto-stand or advance if busted or reached 21
    if (value >= 21) {
      set({ playerTurnComplete: true });
      setTimeout(() => {
        // Read fresh state at callback time — pre-hit state may be stale under splits/rapid input
        const s = get();
        const nextHandIndex = s.currentHandIndex + 1;
        if (nextHandIndex < s.playerHands.length) {
          set({ currentHandIndex: nextHandIndex, playerTurnComplete: false });
        } else {
          get().stand();
        }
      }, 400);
    }
  },

  stand: () => {
    const state = get();
    const currentHand = state.playerHands[state.currentHandIndex];
    if (!currentHand) return;

    // Analyze the decision BEFORE standing (only if not already busted)
    const { value } = getHandValue(currentHand.cards);
    if (value <= 21) {
      const analysis = analyzeDecision(
        currentHand.cards,
        state.dealerHand.cards[0],
        'stand',
        state.settings,
      state.trueCount
      );
      const standDecisionStr = analysis.isCorrect
        ? `stand (correct)`
        : `stand (incorrect — should ${analysis.bestAction})`;
      // Append decision to the current hand's log before advancing
      const newPlayerHands = [...state.playerHands];
      newPlayerHands[state.currentHandIndex] = {
        ...currentHand,
        decisions: [...(currentHand.decisions ?? []), standDecisionStr],
      };
      set({ lastDecisionAnalysis: analysis, playerHands: newPlayerHands, playerHand: newPlayerHands[0] });
    }

    // Check if there are more hands to play (from splits)
    const nextHandIndex = state.currentHandIndex + 1;
    if (nextHandIndex < state.playerHands.length) {
      // Move to next hand
      set({ currentHandIndex: nextHandIndex });
    } else {
      // All hands played, proceed to dealer
      set({ playerTurnComplete: true });
      get().dealerPlay();
    }
  },

  doubleDown: () => {
    const state = get();
    if (!state.isPlaying || state.roundComplete || state.playerTurnComplete) return;

    const currentHand = state.playerHands[state.currentHandIndex];
    if (!currentHand || currentHand.cards.length !== 2) return; // Can only double on first two cards
    if (currentHand.bet > state.bankroll) return; // Need enough to double

    // Disallow doubling split aces (common rule)
    if (currentHand.fromSplitAces) return;

    // Check if doubling after split is allowed
    if (state.playerHands.length > 1 && !state.settings.doubleAfterSplit) return;

    // Analyze the decision
    const analysis = analyzeDecision(
      currentHand.cards,
      state.dealerHand.cards[0],
      'double',
      state.settings,
      state.trueCount
    );

    // Double the bet
    const newBet = currentHand.bet * 2;
    const additionalBet = currentHand.bet;

    // Draw exactly one card
    let newDeck = [...state.deck];
    const newDealtCards = [...state.dealtCards];
    const { card: doubleCard, deck: deckAfterDouble, reshuffled: doubleReshuffled } = drawCard(newDeck, state.settings.numDecks);
    newDeck = deckAfterDouble;
    const card = doubleCard;
    const dealtCard = { ...card, faceUp: true };
    newDealtCards.push(dealtCard);

    const newPlayerCards = [...currentHand.cards, dealtCard];

    // Update count (reset on mid-round reshuffle)
    const newRunningCount = doubleReshuffled ? getCardValue(card.rank, state.settings.countingSystem) : state.runningCount + getCardValue(card.rank, state.settings.countingSystem);
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    // Update the current hand in playerHands array (append decision log)
    const doubleDecisionStr = analysis.isCorrect
      ? `double (correct)`
      : `double (incorrect — should ${analysis.bestAction})`;
    const newPlayerHands = [...state.playerHands];
    newPlayerHands[state.currentHandIndex] = {
      ...currentHand,
      cards: newPlayerCards,
      bet: newBet,
      decisions: [...(currentHand.decisions ?? []), doubleDecisionStr],
    };

    const newBankroll = state.bankroll - additionalBet;
    set({
      deck: newDeck,
      dealtCards: newDealtCards,
      playerHand: newPlayerHands[0], // Keep playerHand in sync
      playerHands: newPlayerHands,
      bankroll: newBankroll,
      runningCount: newRunningCount,
      trueCount: newTrueCount,
      lastDecisionAnalysis: analysis,
      statistics: updateStatsWithBankroll(state.statistics, newBankroll)
    });

    // After doubling, automatically stand (move to next hand or dealer)
    setTimeout(() => {
      get().stand();
    }, 500);
  },

  split: () => {
    const state = get();
    if (!state.isPlaying || state.roundComplete || state.playerTurnComplete) return;

    const currentHand = state.playerHands[state.currentHandIndex];
    if (!currentHand || currentHand.cards.length !== 2) return;

    // Check if cards can be split (same rank)
    const [card1, card2] = currentHand.cards;
    const rank1 = card1.rank === '10' || card1.rank === 'J' || card1.rank === 'Q' || card1.rank === 'K' ? '10' : card1.rank;
    const rank2 = card2.rank === '10' || card2.rank === 'J' || card2.rank === 'Q' || card2.rank === 'K' ? '10' : card2.rank;
    if (rank1 !== rank2) return;

    // Check if we can afford to split
    if (currentHand.bet > state.bankroll) return;

    // Check for resplit aces restriction
    const isAces = card1.rank === 'A';
    if (isAces && state.playerHands.length > 1 && !state.settings.resplitAces) return;

    // Analyze the decision
    const analysis = analyzeDecision(
      currentHand.cards,
      state.dealerHand.cards[0],
      'split',
      state.settings,
      state.trueCount
    );

    // Create two new hands from the split
    let newDeck = [...state.deck];
    const newDealtCards = [...state.dealtCards];
    const originalBet = currentHand.bet;

    // Deal one card to each split hand
    const { card: card1New, deck: deckAfterSplit1, reshuffled: splitReshuffled1 } = drawCard(newDeck, state.settings.numDecks);
    newDeck = deckAfterSplit1;
    const card1NewDealt = { ...card1New, faceUp: true };
    newDealtCards.push(card1NewDealt);

    const { card: card2New, deck: deckAfterSplit2, reshuffled: splitReshuffled2 } = drawCard(newDeck, state.settings.numDecks);
    newDeck = deckAfterSplit2;
    const card2NewDealt = { ...card2New, faceUp: true };
    newDealtCards.push(card2NewDealt);

    // Play split sound
    sounds.playSplit();

    // Record the split decision on the originating hand, then create fresh child hands
    const splitDecisionStr = analysis.isCorrect
      ? `split (correct)`
      : `split (incorrect — should ${analysis.bestAction})`;
    // hand1 carries the originating hand's decision history plus the "split" action
    const hand1: Hand = {
      cards: [card1, card1NewDealt],
      bet: originalBet,
      fromSplit: true,
      fromSplitAces: isAces || currentHand.fromSplitAces,
      decisions: [...(currentHand.decisions ?? []), splitDecisionStr],
    };
    // hand2 is a fresh child hand with its own empty decision log
    const hand2: Hand = {
      cards: [card2, card2NewDealt],
      bet: originalBet,
      fromSplit: true,
      fromSplitAces: isAces || currentHand.fromSplitAces,
      decisions: [],
    };

    // Update count for the two new cards (reset on mid-round reshuffle)
    const card1Delta = getCardValue(card1New.rank, state.settings.countingSystem);
    const card2Delta = getCardValue(card2New.rank, state.settings.countingSystem);
    // Apply each draw's reshuffle independently. A reshuffle resets the running count to
    // 0 (the old shoe's cards are gone), then we add only cards seen from the new shoe.
    // This matters when the deck is exhausted *between* the two split draws: card1 came
    // from the old shoe and must not be carried onto the post-reshuffle baseline.
    let splitRunningCount = splitReshuffled1 ? 0 : state.runningCount;
    splitRunningCount += card1Delta;
    if (splitReshuffled2) splitRunningCount = 0;
    splitRunningCount += card2Delta;
    const newRunningCount = splitRunningCount;
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    // Update playerHands array - replace current hand with hand1, add hand2
    const newPlayerHands = [...state.playerHands];
    newPlayerHands[state.currentHandIndex] = hand1;
    newPlayerHands.splice(state.currentHandIndex + 1, 0, hand2);

    // Update statistics
    const newStats = { ...state.statistics };
    newStats.splitHandsPlayed++;

    const newBankroll = state.bankroll - originalBet;
    set({
      deck: newDeck,
      dealtCards: newDealtCards,
      playerHand: hand1, // Keep playerHand in sync
      playerHands: newPlayerHands,
      bankroll: newBankroll,
      runningCount: newRunningCount,
      trueCount: newTrueCount,
      lastDecisionAnalysis: analysis,
      statistics: updateStatsWithBankroll(newStats, newBankroll)
    });

    // For split aces: enforce one-card only on each resulting hand and auto-advance
    if (isAces) {
      setTimeout(() => {
        get().stand();
      }, 500);
    }
  },

  surrender: () => {
    const state = get();
    if (!state.isPlaying || state.roundComplete || state.playerTurnComplete) return;
    if (!(state.settings.surrenderAllowed || state.settings.earlySurrenderAllowed)) return;

    const currentHand = state.playerHands[state.currentHandIndex];
    if (!currentHand || currentHand.cards.length !== 2) return; // Can only surrender on first two cards

    // Can't surrender after split (late surrender only on initial hand)
    if (state.playerHands.length > 1) return;

    // Late surrender cannot surrender against dealer blackjack
    const dealerCardsForCheck = [...state.dealerHand.cards];
    const dealerHasHole = dealerCardsForCheck[1];
    const dealerUp = dealerCardsForCheck[0];
    const dealerBJNow = dealerUp && dealerHasHole && isBlackjack([dealerUp, dealerHasHole]);
    if (!state.settings.earlySurrenderAllowed && dealerBJNow) return;

    // Analyze the decision
    const analysis = analyzeDecision(
      currentHand.cards,
      state.dealerHand.cards[0],
      'surrender',
      state.settings,
      state.trueCount
    );

    // Return half the bet
    const returnAmount = currentHand.bet / 2;

    // Update statistics
    const newStats = { ...state.statistics };
    newStats.handsPlayed++;
    newStats.handsLost++;
    newStats.totalWagered += currentHand.bet;
    newStats.totalWon -= currentHand.bet / 2; // Surrender loses half the bet
    newStats.biggestLoss = Math.max(newStats.biggestLoss, currentHand.bet / 2);

    // Update the hand with result (append surrender decision log)
    const surrenderDecisionStr = analysis.isCorrect
      ? `surrender (correct)`
      : `surrender (incorrect — should ${analysis.bestAction})`;
    const newPlayerHands = [...state.playerHands];
    newPlayerHands[state.currentHandIndex] = {
      ...currentHand,
      result: 'loss',
      decisions: [...(currentHand.decisions ?? []), surrenderDecisionStr],
    };

    const newBankroll = state.bankroll + returnAmount;
    const finalize = (dealerHandCards: typeof state.dealerHand.cards, runningCount: number, trueCount: number, isDealing: boolean) => {
    set({
      playerHand: newPlayerHands[0],
      playerHands: newPlayerHands,
      bankroll: newBankroll,
      isPlaying: false,
        isDealing,
      playerTurnComplete: true,
      roundComplete: true,
        insuranceOffered: false,
        dealerHand: { ...state.dealerHand, cards: dealerHandCards },
        runningCount,
        trueCount,
      lastDecisionAnalysis: analysis,
      statistics: updateStatsWithBankroll(newStats, newBankroll)
    });
      restoreDebugSettingsIfNeeded(get, set);
    };

    // Smoothly reveal dealer hole card before finalizing
    const dealerCards = [...state.dealerHand.cards];
    const hasHoleToFlip = dealerCards[1] && !dealerCards[1].faceUp;
    if (hasHoleToFlip) {
      set({ isDealing: true, insuranceOffered: false });
      setTimeout(() => {
        let runningCount = state.runningCount;
        dealerCards[1] = { ...dealerCards[1], faceUp: true };
        runningCount += getCardValue(dealerCards[1].rank, state.settings.countingSystem);
        const decksRemaining = getDecksRemaining(state.deck.length);
        const trueCount = calculateTrueCount(runningCount, decksRemaining);
        finalize(dealerCards, runningCount, trueCount, false);
      }, 700);
    } else {
      const runningCount = state.runningCount;
      const decksRemaining = getDecksRemaining(state.deck.length);
      const trueCount = calculateTrueCount(runningCount, decksRemaining);
      finalize(dealerCards, runningCount, trueCount, false);
    }
  },

  takeInsurance: () => {
    const state = get();
    if (!state.insuranceOffered || state.insuranceBet > 0) return;

    // Insurance costs half of the original bet
    const insuranceCost = state.playerHands[0].bet / 2;
    if (insuranceCost > state.bankroll) return;

    // Analyze the insurance decision
    const insuranceAnalysis = analyzeInsuranceDecision('take', state.trueCount);

    const newBankroll = state.bankroll - insuranceCost;
    set({
      insuranceBet: insuranceCost,
      bankroll: newBankroll,
      insuranceOffered: false,
      lastInsuranceAnalysis: insuranceAnalysis,
      statistics: updateStatsWithBankroll(state.statistics, newBankroll)
    });

    // Reveal dealer hole on insurance decision; if dealer has BJ, resolve immediately
    const resolved = revealDealerHoleAndMaybeResolveBJ(get, set);
    if (resolved) return;

    // Now check for player blackjack
    const currentState = get();
    if (isBlackjack(currentState.playerHand.cards)) {
        get().stand();
    }
  },

  declineInsurance: () => {
    const state = get();
    if (!state.insuranceOffered) return;

    // Analyze the insurance decision
    const insuranceAnalysis = analyzeInsuranceDecision('decline', state.trueCount);

    set({
      insuranceOffered: false,
      lastInsuranceAnalysis: insuranceAnalysis
    });

    // Reveal dealer hole on insurance decision; if dealer has BJ, resolve immediately
    const resolved = revealDealerHoleAndMaybeResolveBJ(get, set);
    if (resolved) return;

    // Now check for player blackjack
    const currentState = get();
    if (isBlackjack(currentState.playerHand.cards)) {
        get().stand();
    }
  },

  dealerPlay: () => {
    const state = get();
    if (!state.isPlaying) return;

    // First, flip dealer's hole card
    const dealerCards = [...state.dealerHand.cards];
    dealerCards[1] = { ...dealerCards[1], faceUp: true };

    // Update count with hole card
    const newRunningCount = state.runningCount + getCardValue(dealerCards[1].rank, state.settings.countingSystem);
    const decksRemaining = getDecksRemaining(state.deck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    // Update state with flipped card
    set({
      dealerHand: { ...state.dealerHand, cards: dealerCards },
      runningCount: newRunningCount,
      trueCount: newTrueCount
    });

    // Check if all player hands are busted
    const allBusted = state.playerHands.every(hand => getHandValue(hand.cards).value > 21);

    // If all player hands busted, resolve immediately
    if (allBusted) {
      setTimeout(() => get().resolveDealerHand(), 500);
      return;
    }

    // If all active player hands are blackjacks, skip dealer draw and resolve
    const allBlackjacks = state.playerHands.every(hand => isBlackjack(hand.cards));
    if (allBlackjacks) {
      setTimeout(() => get().resolveDealerHand(), 500);
      return;
    }

    // Check if dealer needs to draw
    const { value, soft } = getHandValue(dealerCards);
    const shouldHit = value < 17 || (value === 17 && soft && state.settings.dealerHitsSoft17);

    if (shouldHit) {
      // Dealer needs to draw - do it one card at a time
      setTimeout(() => get().dealerDrawCard(), 600);
    } else {
      // Dealer stands - resolve hand
      setTimeout(() => get().resolveDealerHand(), 500);
    }
  },

  dealerDrawCard: () => {
    const state = get();
    const dealerCards = [...state.dealerHand.cards];
    let newDeck = [...state.deck];
    const newDealtCards = [...state.dealtCards];

    // Draw one card
    const { card, deck: deckAfterDealer, reshuffled: dealerReshuffled } = drawCard(newDeck, state.settings.numDecks);
    newDeck = deckAfterDealer;
    const dealtCard = { ...card, faceUp: true };
    dealerCards.push(dealtCard);
    newDealtCards.push(dealtCard);

    // Update count (reset on mid-round reshuffle)
    const newRunningCount = dealerReshuffled ? getCardValue(card.rank, state.settings.countingSystem) : state.runningCount + getCardValue(card.rank, state.settings.countingSystem);
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    set({
      deck: newDeck,
      dealtCards: newDealtCards,
      dealerHand: { ...state.dealerHand, cards: dealerCards },
      runningCount: newRunningCount,
      trueCount: newTrueCount
    });

    // Check if dealer needs to draw more
    const { value, soft } = getHandValue(dealerCards);
    const shouldHit = value < 17 || (value === 17 && soft && state.settings.dealerHitsSoft17);

    if (shouldHit && value <= 21) {
      // Draw another card after delay
      setTimeout(() => get().dealerDrawCard(), 600);
    } else {
      // Done drawing - resolve hand
      setTimeout(() => get().resolveDealerHand(), 500);
    }
  },

  resolveDealerHand: () => {
    const state = get();
    const dealerCards = state.dealerHand.cards;
    const dealerValue = getHandValue(dealerCards).value;
    const dealerBusted = dealerValue > 21;
    const dealerBlackjack = isBlackjack(dealerCards);

    // Handle insurance result first
    let insuranceWinnings = 0;
    let insuranceResultVal: 'won' | 'lost' | null = null;
    if (state.insuranceBet > 0) {
      if (dealerBlackjack) {
        insuranceWinnings = state.insuranceBet * 3; // 2:1 payout + original bet back
        insuranceResultVal = 'won';
      } else {
        insuranceWinnings = 0; // Lost insurance bet
        insuranceResultVal = 'lost';
      }
    }

    // Resolve each player hand
    let totalWinnings = insuranceWinnings;
    const newPlayerHands: Hand[] = [];
    const newStats = { ...state.statistics };
    let dealerResult: 'win' | 'loss' | 'push' | 'blackjack' | undefined = undefined;

    for (const hand of state.playerHands) {
      const playerValue = getHandValue(hand.cards).value;
      const playerBusted = playerValue > 21;
      // A true natural: two-card blackjack that did NOT come from a split.
      // Post-split two-card 21 (any split, not just aces) pays even money, not 3:2.
      const isNatural = isBlackjack(hand.cards) && hand.cards.length === 2 && !hand.fromSplit;

      let handResult: 'win' | 'loss' | 'push' | 'blackjack';
      let handWinnings = 0;

      if (playerBusted) {
        handResult = 'loss';
        dealerResult = 'win';
        handWinnings = 0;
      } else if (dealerBusted) {
        handResult = 'win';
        dealerResult = 'loss';
        handWinnings = hand.bet * 2;
      } else if (isNatural && !dealerBlackjack) {
        // True natural blackjack vs no dealer BJ: pays 3:2 (or configured rate)
        handResult = 'blackjack';
        dealerResult = 'loss';
        handWinnings = hand.bet * (1 + state.settings.blackjackPays);
      } else if (dealerBlackjack) {
        // Dealer blackjack: only a true natural pushes; everything else loses
        if (isNatural) {
          handResult = 'push';
          dealerResult = 'push';
          handWinnings = hand.bet;
        } else {
          handResult = 'loss';
          dealerResult = 'blackjack';
          handWinnings = 0;
        }
      } else if (playerValue > dealerValue) {
        // Normal win (includes post-split 21 and split-ace 21 — even money)
        handResult = 'win';
        dealerResult = 'loss';
        handWinnings = hand.bet * 2;
      } else if (dealerValue > playerValue) {
        handResult = 'loss';
        dealerResult = 'win';
        handWinnings = 0;
      } else {
        handResult = 'push';
        dealerResult = 'push';
        handWinnings = hand.bet;
      }

      totalWinnings += handWinnings;
      newPlayerHands.push({ ...hand, result: handResult });

      // Update statistics for this hand
      newStats.handsPlayed++;
      newStats.totalWagered += hand.bet;
      
      // Award XP for playing a hand
      newStats.totalXP += XP_REWARDS.handPlayed;

      if (handResult === 'win' || handResult === 'blackjack') {
        newStats.handsWon++;
        newStats.totalXP += XP_REWARDS.handWon; // Bonus XP for winning
        const profit = handWinnings - hand.bet;
        newStats.totalWon += profit;
        newStats.biggestWin = Math.max(newStats.biggestWin, profit);
      } else if (handResult === 'loss') {
        newStats.handsLost++;
        newStats.totalWon -= hand.bet;
        newStats.biggestLoss = Math.max(newStats.biggestLoss, hand.bet);
      } else {
        newStats.handsPushed++;
      }

      if (handResult === 'blackjack') {
        newStats.blackjacks++;
        newStats.totalXP += XP_REWARDS.blackjack; // Bonus XP for blackjack
      }
      
      // Update level based on new XP
      newStats.level = calculateLevel(newStats.totalXP, state.settings.leveling);
    }

    // Update insurance statistics
    if (insuranceResultVal === 'won') {
      newStats.insuranceBetsWon++;
      newStats.totalWon += state.insuranceBet * 2; // Insurance pays 2:1
    } else if (insuranceResultVal === 'lost') {
      newStats.insuranceBetsLost++;
      newStats.totalWon -= state.insuranceBet; // Lost insurance bet
    }

    // Create hand history entry
    const historyEntry: HandHistoryEntry = {
      id: `hand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      playerHands: newPlayerHands.map(hand => ({
        cards: [...hand.cards],
        bet: hand.bet,
        result: hand.result as 'win' | 'loss' | 'push' | 'blackjack',
        decisions: hand.decisions ?? [],
      })),
      dealerCards: [...dealerCards],
      dealerValue: dealerValue,
      runningCountAtStart: state.runningCount,
      trueCountAtStart: state.trueCount,
      insuranceTaken: state.insuranceBet > 0,
      insuranceResult: insuranceResultVal || undefined
    };

    // Keep history limited to last 100 hands
    const newHistory = [...state.handHistory, historyEntry].slice(-100);

    // Play result sounds
    const hasBlackjack = newPlayerHands.some(h => h.result === 'blackjack');
    const hasWin = newPlayerHands.some(h => h.result === 'win');
    const hasLoss = newPlayerHands.some(h => h.result === 'loss');
    const allPush = newPlayerHands.every(h => h.result === 'push');

    if (hasBlackjack) {
      sounds.playBlackjack();
    } else if (hasWin && !hasLoss) {
      sounds.playWin();
    } else if (hasLoss && !hasWin) {
      sounds.playLoss();
    } else if (allPush) {
      sounds.playPush();
    }

    const totalBetStaked = state.playerHands.reduce((sum, h) => sum + (h.bet || 0), 0);
    // Insurance stake was deducted from bankroll in takeInsurance but is not part of
    // totalBetStaked, while its payout IS folded into totalWinnings — subtract it so the
    // displayed round net reflects the true P/L (e.g. losing hand fully covered by a
    // winning insurance bet nets 0, not +insuranceBet).
    const netResult = totalWinnings - totalBetStaked - state.insuranceBet;
    const newBankroll = state.bankroll + totalWinnings;

    // Restore base bet for next hand (avoid keeping doubled bet)
    const desiredBaseBet = state.lastBaseBet ?? state.settings.minBet;
    const affordableBaseBet = Math.min(desiredBaseBet, newBankroll, state.settings.maxBet);
    const nextBaseBet =
      affordableBaseBet >= state.settings.minBet ? affordableBaseBet : (newBankroll >= state.settings.minBet ? state.settings.minBet : 0);

    // On loss, clear table bet display; still remember base bet for next round
    const normalizedPlayerHands =
      netResult < 0
        ? newPlayerHands.map((h, idx) => (idx === 0 ? { ...h, bet: 0 } : h))
        : newPlayerHands.map((h, idx) => (idx === 0 ? { ...h, bet: nextBaseBet } : h));

    set({
      dealerHand: { ...state.dealerHand, result: dealerResult },
      playerHand: normalizedPlayerHands[0],
      playerHands: normalizedPlayerHands,
      isPlaying: false,
      isDealing: false,
      roundComplete: true,
      bankroll: newBankroll,
      lastBaseBet: nextBaseBet > 0 ? nextBaseBet : state.settings.minBet,
      statistics: updateStatsWithBankroll(newStats, newBankroll),
      insuranceResult: insuranceResultVal,
      handHistory: newHistory,
      lastRoundNet: netResult
    });

    restoreDebugSettingsIfNeeded(get, set);

    // Check achievements after hand
    setTimeout(() => get().checkAchievements(), 100);
  },

  submitCount: (userCount) => {
    const state = get();
    const actualCount = state.runningCount;
    const isCorrect = userCount === actualCount;
    const now = Date.now();

    const newStats = { ...state.statistics };
    
    // Increment count attempts - this tracks how many times user submitted a count
    newStats.countAttempts = (newStats.countAttempts || 0) + 1;
    
    if (!isCorrect) {
      newStats.countErrors++;
      newStats.consecutiveCorrect = 0;
    } else {
      newStats.consecutiveCorrect++;
      newStats.bestStreak = Math.max(newStats.bestStreak, newStats.consecutiveCorrect);

      // Track response time if we have a previous attempt
      if (state.speedDrill.lastAttemptTime) {
        const responseTime = now - state.speedDrill.lastAttemptTime;
        if (newStats.fastestCorrectTime === null || responseTime < newStats.fastestCorrectTime) {
          newStats.fastestCorrectTime = responseTime;
        }
        // Update average response time
        const totalCorrect = newStats.countAttempts - newStats.countErrors;
        if (totalCorrect > 0) {
          newStats.averageResponseTime =
            (newStats.averageResponseTime * (totalCorrect - 1) + responseTime) / totalCorrect;
        }
      }
    }

    // Calculate accuracy percentage based on actual count attempts, not hands played
    if (newStats.countAttempts > 0) {
      newStats.countAccuracy = ((newStats.countAttempts - newStats.countErrors) / newStats.countAttempts) * 100;
    }

    // Speed drill scoring
    const newSpeedDrill = { ...state.speedDrill };
    if (state.speedDrill.isActive && isCorrect) {
      newSpeedDrill.score++;
    }
    newSpeedDrill.lastAttemptTime = now;

    set({
      userInputCount: userCount,
      statistics: newStats,
      speedDrill: newSpeedDrill
    });
  },

  resetGame: () => {
    const { settings } = get();
    const { deck, seed } = createSeededShoe(settings.numDecks);
    set({
      deck,
      deckSeed: seed,
      dealtCards: [],
      playerHand: { cards: [], bet: 0 },
      playerHands: [{ cards: [], bet: 0 }],
      currentHandIndex: 0,
      dealerHand: { cards: [], bet: 0 },
      runningCount: 0,
      trueCount: 0,
      userInputCount: undefined,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false,
      bankroll: settings.startingBankroll,
      lastBaseBet: settings.minBet,
      lastRoundNet: 0,
      insuranceBet: 0,
      insuranceOffered: false,
      insuranceResult: null
    });
  },

  resetStatistics: () => {
    const settings = get().settings;
    const freshStats = { ...DEFAULT_STATISTICS, sessionStartTime: Date.now() };
    set({
      statistics: freshStats,
      speedDrill: buildSpeedDrillDefaults(settings, 0),
      handHistory: [],
      achievements: []
    });
    // Queue saves to Firebase (stats + achievements)
    queueStatsSave(freshStats);
    queueAchievementsSave([]);
    // History and session are saved via subscriber to localStorage
  },

  resetBankroll: () => {
    const { settings } = get();
    set({ bankroll: settings.startingBankroll, lastRoundNet: 0, lastBaseBet: settings.minBet });
  },

  toggleAutoDeal: () => {
    set((state) => ({ autoDeal: !state.autoDeal }));
  },

  setDealSpeed: (speed: number) => {
    set({ dealSpeed: speed });
  },

  dealCard: () => {
    const state = get();
    const { speedDrill, speedDrillDeck, settings } = state;

    // Use speedDrillDeck when speed drill is active
    if (speedDrill.isActive) {
      if (speedDrillDeck.deck.length === 0) {
        // Reshuffle speed drill deck
        const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
        set({
          speedDrillDeck: {
            deck: newDeck,
            deckSeed: newSeed,
            dealtCards: [],
            runningCount: 0
          }
        });
        return;
      }

      // Check penetration for speed drill
      const cardsDealt = settings.numDecks * 52 - speedDrillDeck.deck.length;
      const penetrationReached = (cardsDealt / (settings.numDecks * 52)) * 100;

      if (penetrationReached >= settings.penetration) {
        // Reshuffle speed drill deck
        const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
        set({
          speedDrillDeck: {
            deck: newDeck,
            deckSeed: newSeed,
            dealtCards: [],
            runningCount: 0
          }
        });
        return;
      }

      const newDeck = [...speedDrillDeck.deck];
      const card = newDeck.pop()!;
      const dealtCard = { ...card, faceUp: true };

      const newRunningCount = speedDrillDeck.runningCount + getCardValue(card.rank, settings.countingSystem);

      set({
        speedDrillDeck: {
          deck: newDeck,
          dealtCards: [...speedDrillDeck.dealtCards, dealtCard],
          deckSeed: speedDrillDeck.deckSeed,
          runningCount: newRunningCount
        }
      });
      return;
    }

    // Original logic for non-speed drill mode
    if (state.deck.length === 0) {
      get().reshuffleShoe();
      return;
    }

    // Check penetration
    const cardsDealt = settings.numDecks * 52 - state.deck.length;
    const penetrationReached = (cardsDealt / (settings.numDecks * 52)) * 100;

    if (penetrationReached >= settings.penetration) {
      get().reshuffleShoe();
      return;
    }

    const newDeck = [...state.deck];
    const card = newDeck.pop()!;
    const dealtCard = { ...card, faceUp: true };

    const newRunningCount = state.runningCount + getCardValue(card.rank, settings.countingSystem);
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    set({
      deck: newDeck,
      dealtCards: [...state.dealtCards, dealtCard],
      playerHand: { ...state.playerHand, cards: [...state.playerHand.cards, dealtCard] },
      runningCount: newRunningCount,
      trueCount: newTrueCount
    });
  },

  startSpeedDrill: () => {
    const state = get();
    const { deck: newDeck, seed: newSeed } = createSeededShoe(state.settings.numDecks);
    // Set all state at once to avoid reshuffleShoe overwriting speedDrill
    // Use speedDrillDeck instead of main deck
    set({
      speedDrill: {
        isActive: true,
        isPaused: false,
        timer: state.speedDrill.duration,
        duration: state.speedDrill.duration,
        speed: state.speedDrill.speed,
        score: 0,
        highScore: state.speedDrill.highScore,
        lastScore: null,
        isNewHighScore: false,
        lastAttemptTime: Date.now(),
        isFinished: false
      },
      autoDeal: true,
      speedDrillDeck: {
        deck: newDeck,
        deckSeed: newSeed,
        dealtCards: [],
        runningCount: 0
      }
    });
  },

  stopSpeedDrill: () => {
    const state = get();
    const newHighScore = Math.max(state.speedDrill.score, state.speedDrill.highScore);
    set({
      speedDrill: {
        ...state.speedDrill,
        isActive: false,
        isPaused: false,
        highScore: newHighScore,
        isFinished: true
      },
      autoDeal: false
    });
  },

  pauseSpeedDrill: () => {
    const state = get();
    if (!state.speedDrill.isActive) return;
    set({
      speedDrill: {
        ...state.speedDrill,
        isPaused: true
      },
      autoDeal: false
    });
  },

  resumeSpeedDrill: () => {
    const state = get();
    if (!state.speedDrill.isActive || !state.speedDrill.isPaused) return;
    set({
      speedDrill: {
        ...state.speedDrill,
        isPaused: false
      },
      autoDeal: true
    });
  },

  setSpeedDrillSpeed: (speed: number) => {
    const state = get();
    set({
      speedDrill: {
        ...state.speedDrill,
        speed
      }
    });
  },

  setSpeedDrillDuration: (duration: number) => {
    const state = get();
    set({
      speedDrill: {
        ...state.speedDrill,
        duration,
        // Also update timer if not currently active
        timer: state.speedDrill.isActive ? state.speedDrill.timer : duration
      }
    });
  },

  setSpeedDrillScore: (cardsDealt: number, speed: number, isCorrect: boolean) => {
    const state = get();
    
    // Track drill completion and award XP
    const newStats = { ...state.statistics };
    newStats.drillsCompleted++;
    newStats.totalXP += XP_REWARDS.drillCompleted;
    
    if (!isCorrect) {
      // Wrong answer = no score, but still counts as completed
      newStats.level = calculateLevel(newStats.totalXP, state.settings.leveling);
      persistStats(newStats);
      
      set({
        speedDrill: {
          ...state.speedDrill,
          lastScore: 0,
          isNewHighScore: false
        },
        statistics: newStats
      });
      return;
    }

    // Calculate score: cards dealt × speed multiplier
    // Speed multiplier: 1000ms = 1x, 500ms = 2x, 333ms = 3x, etc.
    const speedMultiplier = 1000 / speed;
    const drillScore = Math.round(cardsDealt * speedMultiplier);
    
    // Award bonus XP based on score (score/10)
    newStats.totalXP += Math.floor(drillScore / 10);
    newStats.level = calculateLevel(newStats.totalXP, state.settings.leveling);
    
    // Check if it's a new high score
    const isNewHighScore = drillScore > state.speedDrill.highScore;
    const newHighScore = isNewHighScore ? drillScore : state.speedDrill.highScore;
    // Note: High score is persisted to localStorage via subscriber (device-specific)
    
    // Persist stats to Firebase
    persistStats(newStats);

    set({
      speedDrill: {
        ...state.speedDrill,
        lastScore: drillScore,
        highScore: newHighScore,
        isNewHighScore
      },
      statistics: newStats
    });
  },

  tickSpeedDrill: () => {
    const state = get();
    if (!state.speedDrill.isActive || state.speedDrill.isPaused) return;

    const newTimer = state.speedDrill.timer - 1;
    if (newTimer <= 0) {
      get().stopSpeedDrill();
    } else {
      set({
        speedDrill: {
          ...state.speedDrill,
          timer: newTimer
        }
      });
    }
  },

  updateTableConfig: (newConfig) => {
    const state = get();
    const config = { ...state.tableConfig, ...newConfig };
    const newSeats = createSeats(config);
    set({
      tableConfig: config,
      seats: newSeats
    });
  },

  startTableRound: () => {
    const state = get();
    const { tableSimDeck, settings, seats } = state;

    // Check shuffle using table sim's separate deck
    const totalCards = settings.numDecks * 52;
    if (shouldReshuffle(tableSimDeck.deck, totalCards, settings.penetration) || settings.shuffleAfterHand) {
      // Reshuffle only the table sim deck
      const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
      set({
        tableSimDeck: {
          deck: newDeck,
          deckSeed: newSeed,
          dealtCards: [],
          runningCount: 0,
          trueCount: 0,
          dealerHand: { cards: [], bet: 0 }
        }
      });
      return;
    }

    if (tableSimDeck.deck.length < 20) {
      // Reshuffle only the table sim deck
      const { deck: newDeck, seed: newSeed } = createSeededShoe(settings.numDecks);
      set({
        tableSimDeck: {
          deck: newDeck,
          deckSeed: newSeed,
          dealtCards: [],
          runningCount: 0,
          trueCount: 0,
          dealerHand: { cards: [], bet: 0 }
        }
      });
      return;
    }

    // Reset seats for new round
    const newSeats = seats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));

    // Reset dealer for table sim
    const newDealerHand: Hand = { cards: [], bet: 0, result: undefined };

    set({
      seats: newSeats,
      tableSimDeck: {
        ...tableSimDeck,
        dealerHand: newDealerHand
      },
      tableState: {
        dealingPhase: 'dealing-initial',
        currentSeatIndex: 0, // Start with first seat for initial deal
        currentHandIndex: 0,
        isAutoPlaying: state.autoDeal,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
  },

  advanceTablePhase: () => {
    const state = get();
    const { tableSimDeck, seats, tableState, settings } = state;
    const { dealingPhase, currentSeatIndex } = tableState;

    // Use table sim's separate deck state
    let newDeck = [...tableSimDeck.deck];
    const newDealtCards = [...tableSimDeck.dealtCards];
    // Deep-copy seats (including nested hands and cards arrays) so dealCardTo's push
    // does not mutate the original seat objects' arrays in place.
    let newSeats = seats.map(seat => ({
      ...seat,
      hands: seat.hands.map(hand => ({
        ...hand,
        cards: [...hand.cards],
      })),
    }));
    // Deep copy dealer hand including cards array to avoid mutation
    const newDealerHand = {
      ...tableSimDeck.dealerHand,
      cards: [...tableSimDeck.dealerHand.cards]
    };
    let newRunningCount = tableSimDeck.runningCount;
    let nextPhase = dealingPhase;
    let nextSeatIndex = currentSeatIndex;

    const dealCardTo = (target: 'player' | 'dealer', seatIdx?: number, faceUp: boolean = true) => {
      const { card, deck: deckAfterDeal, reshuffled: dealReshuffled } = drawCard(newDeck, settings.numDecks);
      newDeck = deckAfterDeal;
      if (dealReshuffled) newRunningCount = 0;
      const dealtCard = { ...card, faceUp };
      newDealtCards.push(dealtCard);

      if (faceUp) {
        newRunningCount += getCardValue(card.rank, settings.countingSystem);
      }

      if (target === 'player' && seatIdx !== undefined) {
        // Default to first hand for now, split logic will need to specify hand index
        // For dealing initial cards, it's always the first hand (index 0)
        newSeats[seatIdx].hands[0].cards.push(dealtCard);
      } else {
        newDealerHand.cards.push(dealtCard);
      }
    };

    // Helper to determine next phase based on seat
    const getPhaseForSeat = (seatIdx: number) => {
      if (seatIdx >= newSeats.length) return 'dealer-turn';
      const seat = newSeats[seatIdx];
      if (!seat.isActive) return getPhaseForSeat(seatIdx + 1);
      return seat.playerType === 'ai' ? 'ai-turn' : 'player-turn';
    };

    // Helper to find next active seat index starting from a given index
    const findNextActiveSeat = (startIdx: number): number => {
      let idx = startIdx;
      while (idx < newSeats.length && !newSeats[idx].isActive) {
        idx++;
      }
      return idx;
    };

    switch (dealingPhase) {
      case 'dealing-initial': {
        const activeSeats = newSeats.filter(s => s.isActive);

        // Check how many cards everyone has
        const minCards = Math.min(
          ...activeSeats.map(s => s.hands[0].cards.length),
          newDealerHand.cards.length < 2 ? newDealerHand.cards.length : 2
        );

        if (minCards === 0) {
          // Dealing first card - find current active seat
          const activeSeatIdx = findNextActiveSeat(currentSeatIndex);

          if (activeSeatIdx < newSeats.length) {
            dealCardTo('player', activeSeatIdx);
            // Find next active seat for next iteration
            nextSeatIndex = findNextActiveSeat(activeSeatIdx + 1);
          } else {
            // All players dealt, deal to dealer
            dealCardTo('dealer');
            nextSeatIndex = findNextActiveSeat(0); // Reset for second pass, starting at first active
          }
        } else if (minCards === 1) {
          // Dealing second card - find current active seat
          const activeSeatIdx = findNextActiveSeat(currentSeatIndex);

          if (activeSeatIdx < newSeats.length) {
            dealCardTo('player', activeSeatIdx);
            // Find next active seat for next iteration
            nextSeatIndex = findNextActiveSeat(activeSeatIdx + 1);
          } else {
            // Deal to dealer (face down)
            dealCardTo('dealer', undefined, false);

            // If dealer shows Ace and insurance is allowed, pause to offer insurance
            const dealerUpCard = newDealerHand.cards[0];
            const userSeatIndex = newSeats.findIndex(s => s.playerType === 'user' && s.isActive);
            const userHasBlackjack = userSeatIndex >= 0 && isBlackjack(newSeats[userSeatIndex].hands[0].cards);
            const offerInsurance = settings.insuranceAllowed && dealerUpCard?.rank === 'A' && userSeatIndex >= 0 && !userHasBlackjack;

            if (offerInsurance) {
              nextPhase = 'insurance-offer';
              // Focus on user seat for prompt
              nextSeatIndex = userSeatIndex;
            } else {
              // If dealer shows 10-value, peek for blackjack before player turns
              const dealerShowsTen = dealerUpCard && ['10', 'J', 'Q', 'K'].includes(dealerUpCard.rank);
              if (dealerShowsTen) {
                const hole = newDealerHand.cards[1];
                const dealerBJ = isBlackjack([dealerUpCard, { ...hole, faceUp: true }]);
                if (dealerBJ) {
                  // Resolve immediately, but keep hole face down briefly for visual timing
                  newSeats = newSeats.map(seat => {
                    if (!seat.isActive) return seat;
                    const playerValue = getHandValue(seat.hands[0].cards).value;
                    const playerBJ = isBlackjack(seat.hands[0].cards);
                    let handResult: 'win' | 'loss' | 'push' | 'blackjack';
                    if (playerValue > 21) {
                      handResult = 'loss';
                    } else if (playerBJ) {
                      handResult = 'push';
                    } else {
                      handResult = 'loss';
                    }
                    return {
                      ...seat,
                      hands: [
                        { ...seat.hands[0], result: handResult },
                        ...seat.hands.slice(1)
                      ]
                    };
                  });
                  nextPhase = 'showing-results';
                  nextSeatIndex = currentSeatIndex;

                  // After a short delay, flip the hole card and update counts
                  setTimeout(() => {
                    const stateAfter = get();
                    const { tableSimDeck: tsd, tableState: ts } = stateAfter;
                    if (ts.dealingPhase !== 'showing-results') return;
                    const dh = tsd.dealerHand;
                    if (!dh.cards[1] || dh.cards[1].faceUp) return;
                    const flippedHole = { ...dh.cards[1], faceUp: true };
                    const updatedRunning = tsd.runningCount + getCardValue(flippedHole.rank, stateAfter.settings.countingSystem);
                    const decksRemaining = getDecksRemaining(tsd.deck.length);
                    const updatedTrue = calculateTrueCount(updatedRunning, decksRemaining);
                    set({
                      tableSimDeck: {
                        ...tsd,
                        dealerHand: {
                          ...dh,
                          cards: [dh.cards[0], flippedHole]
                        },
                        runningCount: updatedRunning,
                        trueCount: updatedTrue
                      }
                    });
                  }, 600);
                  break;
                }
              }

              // Find first active player for their turn, skipping those with Blackjack
              let firstTurnIndex = findNextActiveSeat(0);
              while (firstTurnIndex < newSeats.length) {
                const seat = newSeats[firstTurnIndex];
                if (!isBlackjack(seat.hands[0].cards)) {
                  // Found an active seat without Blackjack
                  break;
                }
                firstTurnIndex = findNextActiveSeat(firstTurnIndex + 1);
              }

              nextSeatIndex = firstTurnIndex;
              if (nextSeatIndex >= newSeats.length) {
                nextPhase = 'dealer-turn';
              } else {
                nextPhase = getPhaseForSeat(nextSeatIndex);
              }
            }
          }
        }
        break;
      }

      case 'insurance-offer': {
        // Wait for player to choose insurance; stay in this phase
        nextPhase = 'insurance-offer';
        break;
      }

      case 'player-turn':
      case 'ai-turn': {
        const currentSeat = newSeats[currentSeatIndex];

        if (!currentSeat || !currentSeat.isActive) {
          // Move to next seat
          nextSeatIndex++;
          if (nextSeatIndex >= newSeats.length) {
            nextPhase = 'dealer-turn';
          } else {
            nextPhase = getPhaseForSeat(nextSeatIndex);
            // If the next seat is inactive (handled by recursion in getPhaseForSeat logic above, 
            // but we need to update nextSeatIndex to the actual active one)
            // Let's simplify: just increment index, and let the next loop handle skipping inactive
            // Actually, better to skip inactive immediately to avoid empty frames
            while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
              nextSeatIndex++;
            }
            if (nextSeatIndex >= newSeats.length) {
              nextPhase = 'dealer-turn';
            } else {
              nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
            }
          }
          break;
        }

        // Check if player has blackjack - auto-skip to next seat
        if (isBlackjack(currentSeat.hands[0].cards)) {
          // Player has blackjack, skip to next seat
          nextSeatIndex++;
          while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
            nextSeatIndex++;
          }
          if (nextSeatIndex >= newSeats.length) {
            nextPhase = 'dealer-turn';
          } else {
            nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
          }
          break;
        }

        // If it's AI turn, make a move
        if (currentSeat.playerType === 'ai') {
          nextPhase = 'ai-turn'; // Ensure phase is correct
          // AI only plays one hand for now
          const applyAiMistake = (aiDecision: 'hit' | 'stand' | 'double' | 'surrender' | 'split') => {
            const rate = Math.max(0, Math.min(1, settings.aiMistakeRate ?? 0));
            if (rate <= 0) return aiDecision;
            if (Math.random() > rate) return aiDecision;

            // Allow occasional bad split of tens
            const hand = currentSeat.hands[0];
            const hasTwoTens = hand.cards.length === 2 &&
              ['10', 'J', 'Q', 'K'].includes(hand.cards[0].rank) &&
              ['10', 'J', 'Q', 'K'].includes(hand.cards[1].rank);

            if (aiDecision === 'stand' && hasTwoTens) {
              return 'split';
            }

            if (aiDecision === 'hit') return 'stand';
            if (aiDecision === 'stand') return 'hit';
            return aiDecision; // keep doubles/splits/surrender correct
          };

          const decision = applyAiMistake(getBasicStrategyDecision(currentSeat.hands[0].cards, newDealerHand.cards[0]));

          if (decision === 'stand') {
            nextSeatIndex++;
            // Skip inactive
            while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
              nextSeatIndex++;
            }
            if (nextSeatIndex >= newSeats.length) {
              nextPhase = 'dealer-turn';
            } else {
              nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
            }
          } else {
            // Hit or Double (AI doesn't double in this basic version yet, but let's treat as hit)
            // Actually basic strategy might return double. If so, hit for now or implement double.
            // The getBasicStrategyDecision returns 'hit' | 'stand' | 'double' | 'surrender'

            if (decision === 'double') {
              // Implement AI double
              dealCardTo('player', currentSeatIndex);
              // AI doubles then stands
              nextSeatIndex++;
              while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
                nextSeatIndex++;
              }
              if (nextSeatIndex >= newSeats.length) {
                nextPhase = 'dealer-turn';
              } else {
                nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
              }
            } else if (decision === 'surrender') {
              // AI Surrender — forfeit half the bet, like the player surrender path.
              newSeats[currentSeatIndex] = {
                ...newSeats[currentSeatIndex],
                hands: [
                  { ...newSeats[currentSeatIndex].hands[0], bet: newSeats[currentSeatIndex].hands[0].bet / 2, result: 'loss' },
                  ...newSeats[currentSeatIndex].hands.slice(1)
                ]
              };
              nextSeatIndex++;
              while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
                nextSeatIndex++;
              }
              if (nextSeatIndex >= newSeats.length) {
                nextPhase = 'dealer-turn';
              } else {
                nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
              }
            } else {
              // Hit
              dealCardTo('player', currentSeatIndex);
              const { value } = getHandValue(currentSeat.hands[0].cards);
              if (value > 21) {
                nextSeatIndex++;
                while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
                  nextSeatIndex++;
                }
                if (nextSeatIndex >= newSeats.length) {
                  nextPhase = 'dealer-turn';
                } else {
                  nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
                }
              } else {
                // Stay on same seat, same phase
                nextPhase = 'ai-turn';
              }
            }
          }
        }
      }
        break;


      case 'dealer-turn': {
        // Flip hole card if not up
        if (!newDealerHand.cards[1].faceUp) {
          // Create new card object with faceUp set to true
          newDealerHand.cards[1] = { ...newDealerHand.cards[1], faceUp: true };
          newRunningCount += getCardValue(newDealerHand.cards[1].rank, settings.countingSystem);
        } else {
          // Draw cards
          const { value, soft } = getHandValue(newDealerHand.cards);
          const shouldHit = value < 17 || (value === 17 && soft && settings.dealerHitsSoft17);

          if (shouldHit && value <= 21) {
            dealCardTo('dealer');
          } else {
            nextPhase = 'resolving';
          }
        }
        break;
      }

      case 'resolving': {
        // Calculate results
        const dealerValue = getHandValue(newDealerHand.cards).value;
        const dealerBusted = dealerValue > 21;
        const dealerBJ = isBlackjack(newDealerHand.cards);

        newSeats.forEach((seat, seatIndex) => {
          if (!seat.isActive) return;
          // Skip if already resolved (surrender)
          // For now, check first hand. Split logic will need to iterate all hands.
          if (seat.hands[0].result) return;

          const playerValue = getHandValue(seat.hands[0].cards).value;
          // A two-card 21 is only a natural (3:2) if it did NOT come from a split.
          const playerBJ = isBlackjack(seat.hands[0].cards) && !seat.hands[0].fromSplit;

          let handResult: 'win' | 'loss' | 'push' | 'blackjack';
          if (playerValue > 21) {
            handResult = 'loss';
          } else if (dealerBusted) {
            handResult = playerBJ ? 'blackjack' : 'win';
          } else if (dealerBJ) {
            handResult = playerBJ ? 'push' : 'loss';
          } else if (playerBJ) {
            handResult = 'blackjack';
          } else if (playerValue > dealerValue) {
            handResult = 'win';
          } else if (playerValue < dealerValue) {
            handResult = 'loss';
          } else {
            handResult = 'push';
          }

          // Create new seat object with updated hand result
          newSeats[seatIndex] = {
            ...seat,
            hands: [
              { ...seat.hands[0], result: handResult },
              ...seat.hands.slice(1)
            ]
          };
        });

        nextPhase = 'showing-results'; // Show results before going to idle
        break;
      }

      case 'showing-results': {
        // Just wait to display results, then go to idle
        nextPhase = 'idle';
        break;
      }
    }

    // Update True Count
    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    set({
      tableSimDeck: {
        deck: newDeck,
        dealtCards: newDealtCards,
        deckSeed: tableSimDeck.deckSeed,
        runningCount: newRunningCount,
        trueCount: newTrueCount,
        dealerHand: newDealerHand
      },
      seats: newSeats,
      tableState: {
        ...tableState,
        dealingPhase: nextPhase,
        currentSeatIndex: nextSeatIndex
      }
    });
  },



  playerTableDecision: (action: 'hit' | 'stand' | 'double' | 'surrender' | 'split') => {
    const state = get();
    const { seats, tableState, tableSimDeck, settings } = state;
    const { currentSeatIndex, currentHandIndex } = tableState;
    const currentSeat = seats[currentSeatIndex];

    if (!currentSeat || currentSeat.playerType !== 'user') return;

    // Play action sound
    switch (action) {
      case 'hit':
      case 'stand':
        sounds.playClick();
        break;
      case 'double':
        sounds.playChip();
        break;
      case 'split':
        sounds.playSplit();
        break;
      case 'surrender':
        sounds.playClick();
        break;
    }

    const currentHand = currentSeat.hands[currentHandIndex];

    // Analyze the decision using table sim's deck state
    const analysis = analyzeDecision(
      currentHand.cards,
      tableSimDeck.dealerHand.cards[0],
      action,
      settings,
      tableSimDeck.trueCount
    );

    let newDeck = [...tableSimDeck.deck];
    const newDealtCards = [...tableSimDeck.dealtCards];
    // Deep copy seats to avoid mutation issues
    const newSeats = seats.map(seat => ({
      ...seat,
      hands: seat.hands.map(hand => ({
        ...hand,
        cards: [...hand.cards]
      }))
    }));
    let newRunningCount = tableSimDeck.runningCount;
    let nextSeatIndex = currentSeatIndex;
    let nextHandIndex = currentHandIndex;
    let nextPhase = tableState.dealingPhase;
    let newTrueCount = tableSimDeck.trueCount;

    const dealCard = () => {
      const { card, deck: deckAfterPlayerDecision, reshuffled: playerDecisionReshuffled } = drawCard(newDeck, settings.numDecks);
      newDeck = deckAfterPlayerDecision;
      if (playerDecisionReshuffled) newRunningCount = 0;
      const dealtCard = { ...card, faceUp: true };
      newDealtCards.push(dealtCard);
      newRunningCount += getCardValue(card.rank, settings.countingSystem);
      newSeats[currentSeatIndex].hands[nextHandIndex].cards.push(dealtCard);
    };

    const moveToNextHandOrSeat = () => {
      // Check if current seat has more hands to play
      if (nextHandIndex < newSeats[currentSeatIndex].hands.length - 1) {
        nextHandIndex++;
        // Deal second card to the next hand if it only has 1 card (from split)
        if (newSeats[currentSeatIndex].hands[nextHandIndex].cards.length === 1) {
          dealCard();
          // Check for 21 on new card
          const { value } = getHandValue(newSeats[currentSeatIndex].hands[nextHandIndex].cards);
          if (value === 21) {
            // Auto-stand on 21
            // We need to be careful here because we are already inside moveToNextHandOrSeat
            // Recursion is risky. Let's just increment nextHandIndex or move to next seat directly?
            // Actually, if we are in the loop, we can just continue?
            // But moveToNextHandOrSeat is called recursively? No, it's not recursive.

            // Better approach: Check this condition at the start of the next turn or handle it here.
            // If we just dealt the second card and it's 21, we should move to the next hand/seat.

            // Let's recursively call moveToNextHandOrSeat to advance.
            // But we need to make sure we don't infinite loop.
            // Since we incremented nextHandIndex, we are making progress.
            moveToNextHandOrSeat();
            return;
          }
        }
      } else {
        // Move to next seat
        nextSeatIndex++;
        nextHandIndex = 0;
        while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
          nextSeatIndex++;
        }
        if (nextSeatIndex >= newSeats.length) {
          nextPhase = 'dealer-turn';
        } else {
          nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
        }
      }
    };

    switch (action) {
      case 'hit': {
        dealCard();
        const { value } = getHandValue(newSeats[currentSeatIndex].hands[nextHandIndex].cards);
        if (value >= 21) {
          moveToNextHandOrSeat();
        }
        break;
      }
      case 'stand':
        moveToNextHandOrSeat();
        break;
      case 'double': {
        const doubleSeat = newSeats[currentSeatIndex];
        const doubleHand = doubleSeat.hands[nextHandIndex];
        newSeats[currentSeatIndex] = {
          ...doubleSeat,
          hands: [
            ...doubleSeat.hands.slice(0, nextHandIndex),
            { ...doubleHand, bet: doubleHand.bet * 2 },
            ...doubleSeat.hands.slice(nextHandIndex + 1)
          ]
        };
        dealCard();
        moveToNextHandOrSeat();
        break;
      }
      case 'surrender': {
        const surrenderSeat = newSeats[currentSeatIndex];
        const surrenderHand = surrenderSeat.hands[nextHandIndex];
        newSeats[currentSeatIndex] = {
          ...surrenderSeat,
          hands: [
            ...surrenderSeat.hands.slice(0, nextHandIndex),
            { ...surrenderHand, bet: surrenderHand.bet / 2, result: 'loss' as const },
            ...surrenderSeat.hands.slice(nextHandIndex + 1)
          ]
        };
        moveToNextHandOrSeat();
        break;
      }
      case 'split': {
        // 1. Create new hand. Mark both hands as post-split so a two-card 21 is
        //    not paid as a natural blackjack (3:2) at resolution.
        const splitCard = newSeats[currentSeatIndex].hands[nextHandIndex].cards.pop()!;
        const newHand = {
          cards: [splitCard],
          bet: newSeats[currentSeatIndex].hands[nextHandIndex].bet,
          fromSplit: true
        };
        newSeats[currentSeatIndex].hands.push(newHand);
        newSeats[currentSeatIndex].hands[nextHandIndex] = {
          ...newSeats[currentSeatIndex].hands[nextHandIndex],
          fromSplit: true
        };

        // 2. Deal card to current hand
        dealCard();

        // 3. If splitting aces, give exactly one card to each hand and move on
        const currentHand = newSeats[currentSeatIndex].hands[nextHandIndex];
        const justCreatedIndex = newSeats[currentSeatIndex].hands.length - 1;
        const splittingAces =
          currentHand.cards[0]?.rank === 'A' &&
          newSeats[currentSeatIndex].hands[justCreatedIndex].cards[0]?.rank === 'A';

        if (splittingAces) {
          // Mark both hands as from split aces to guard future actions if needed
          newSeats[currentSeatIndex].hands[nextHandIndex] = { ...currentHand, fromSplit: true, fromSplitAces: true };
          newSeats[currentSeatIndex].hands[justCreatedIndex] = {
            ...newSeats[currentSeatIndex].hands[justCreatedIndex],
            fromSplit: true,
            fromSplitAces: true
          };

          // Deal one card to the newly created ace hand
          const originalHandIndex = nextHandIndex;
          nextHandIndex = justCreatedIndex;
          dealCard();
          // Restore index to current for advancing logic
          nextHandIndex = originalHandIndex;

          // Advance to next seat (both ace hands are done after one card)
          nextSeatIndex++;
          nextHandIndex = 0;
          while (nextSeatIndex < newSeats.length && !newSeats[nextSeatIndex].isActive) {
            nextSeatIndex++;
          }
          nextPhase = nextSeatIndex >= newSeats.length
            ? 'dealer-turn'
            : newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
        }

        // Stay on current hand (index doesn't change yet) for non-ace splits
        break;
      }
    }

    // Update True Count
    const decksRemaining = getDecksRemaining(newDeck.length);
    newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    set({
      tableSimDeck: {
        deck: newDeck,
        dealtCards: newDealtCards,
        deckSeed: tableSimDeck.deckSeed,
        runningCount: newRunningCount,
        trueCount: newTrueCount,
        dealerHand: tableSimDeck.dealerHand
      },
      seats: newSeats,
      tableState: {
        ...tableState,
        dealingPhase: nextPhase,
        currentSeatIndex: nextSeatIndex,
        currentHandIndex: nextHandIndex,
        lastDecisionAnalysis: analysis
      }
    });
  },

  tableTakeInsurance: () => {
    const state = get();
    const { tableState } = state;
    if (tableState.dealingPhase !== 'insurance-offer') {
      console.debug('[tableTakeInsurance] ignored: phase', tableState.dealingPhase);
      return;
    }
    console.debug('[tableTakeInsurance] clicked in insurance-offer phase');
    get().handleTableInsurance(true);
  },

  tableDeclineInsurance: () => {
    const state = get();
    const { tableState } = state;
    if (tableState.dealingPhase !== 'insurance-offer') {
      console.debug('[tableDeclineInsurance] ignored: phase', tableState.dealingPhase);
      return;
    }
    console.debug('[tableDeclineInsurance] clicked in insurance-offer phase');
    get().handleTableInsurance(false);
  },

  tableInsuranceSurrender: () => {
    const state = get();
    const { seats, tableState, settings } = state;
    if (tableState.dealingPhase !== 'insurance-offer') {
      console.debug('[tableInsuranceSurrender] ignored: phase', tableState.dealingPhase);
      return;
    }
    // Only allow surrender during insurance if early surrender is enabled
    if (!settings.earlySurrenderAllowed) {
      console.debug('[tableInsuranceSurrender] ignored: early surrender disabled');
      return;
    }
    const userIndex = seats.findIndex(s => s.playerType === 'user' && s.isActive);
    if (userIndex < 0) {
      console.debug('[tableInsuranceSurrender] no active user seat');
      return;
    }

    const findNextActiveSeat = (startIdx: number): number => {
      let idx = startIdx;
      while (idx < seats.length && !seats[idx].isActive) {
        idx++;
      }
      return idx;
    };

    const newSeats = seats.map((seat, idx) => {
      if (idx !== userIndex) return seat;
      const hand = seat.hands[0];
      return {
        ...seat,
        isActive: false,
        hands: [
          { ...hand, bet: hand.bet / 2, result: 'loss' as const },
          ...seat.hands.slice(1)
        ]
      };
    });

    const nextSeatIndex = findNextActiveSeat(0);
    let nextPhase: DealingPhase;
    if (nextSeatIndex >= newSeats.length) {
      nextPhase = 'dealer-turn';
    } else {
      nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
    }

    set({
      seats: newSeats,
      tableState: {
        ...tableState,
        dealingPhase: nextPhase,
        currentSeatIndex: nextSeatIndex,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
  },

  handleTableInsurance: (take: boolean) => {
    const state = get();
    const { tableSimDeck, seats, settings, tableState } = state;
    const newDeck = [...tableSimDeck.deck];
    const newDealtCards = [...tableSimDeck.dealtCards];
    let newSeats = [...seats];
    const newDealerHand = {
      ...tableSimDeck.dealerHand,
      cards: [...tableSimDeck.dealerHand.cards]
    };
    let newRunningCount = tableSimDeck.runningCount;

    const findNextActiveSeat = (startIdx: number): number => {
      let idx = startIdx;
      while (idx < newSeats.length && !newSeats[idx].isActive) {
        idx++;
      }
      return idx;
    };

    const holeCard = newDealerHand.cards[1];
    const dealerUp = newDealerHand.cards[0];

    // Peek hole card without flipping unless BJ
    const dealerBJ = isBlackjack([
      dealerUp,
      { ...holeCard, faceUp: true }
    ]);

    let nextPhase: DealingPhase = 'player-turn';
    let nextSeatIndex = tableState.currentSeatIndex;

    if (dealerBJ) {
      // Reveal hole and resolve immediately
      if (!holeCard.faceUp) {
        newDealerHand.cards[1] = { ...holeCard, faceUp: true };
        newRunningCount += getCardValue(holeCard.rank, settings.countingSystem);
      }
      newSeats = newSeats.map(seat => {
        if (!seat.isActive) return seat;
        const playerValue = getHandValue(seat.hands[0].cards).value;
        const playerBJ = isBlackjack(seat.hands[0].cards);
        let handResult: 'win' | 'loss' | 'push' | 'blackjack';
        if (playerValue > 21) {
          handResult = 'loss';
        } else if (playerBJ) {
          handResult = 'push';
        } else {
          handResult = 'loss';
        }
        return {
          ...seat,
          hands: [
            { ...seat.hands[0], result: handResult },
            ...seat.hands.slice(1)
          ]
        };
      });
      nextPhase = 'showing-results';
      nextSeatIndex = tableState.currentSeatIndex;
    } else {
      // Keep hole card face down; continue to first active seat without blackjack
      let firstTurnIndex = findNextActiveSeat(0);
      while (firstTurnIndex < newSeats.length) {
        const seat = newSeats[firstTurnIndex];
        if (!isBlackjack(seat.hands[0].cards)) {
          break;
        }
        firstTurnIndex = findNextActiveSeat(firstTurnIndex + 1);
      }
      nextSeatIndex = firstTurnIndex;
      if (nextSeatIndex >= newSeats.length) {
        nextPhase = 'dealer-turn';
      } else {
        nextPhase = newSeats[nextSeatIndex].playerType === 'ai' ? 'ai-turn' : 'player-turn';
      }
    }

    const decksRemaining = getDecksRemaining(newDeck.length);
    const newTrueCount = calculateTrueCount(newRunningCount, decksRemaining);

    set({
      tableSimDeck: {
        deck: newDeck,
        dealtCards: newDealtCards,
        deckSeed: tableSimDeck.deckSeed,
        runningCount: newRunningCount,
        trueCount: newTrueCount,
        dealerHand: newDealerHand
      },
      seats: newSeats,
      tableState: {
        ...tableState,
        dealingPhase: nextPhase,
        currentSeatIndex: nextSeatIndex,
        insuranceOffered: true,
        insuranceTaken: take
      }
    });
  },

  debugDealDealerAceTraining: () => {
    const state = get();
    const { settings } = state;
    const stackCards = [
      makeCard('dbg-da-hole', '9', '♦', false),   // dealer hole
      makeCard('dbg-da-p2', '9', '♣', true),     // player 2nd
      makeCard('dbg-da-up', 'A', '♣', true),     // dealer up
      makeCard('dbg-da-p1', '10', '♠', true)     // player 1st
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealPlayerBJTraining: () => {
    const state = get();
    const { settings } = state;
    const stackCards = [
      makeCard('dbg-pbj-hole', '7', '♠', false),  // dealer hole
      makeCard('dbg-pbj-p2', 'K', '♥', true),    // player second
      makeCard('dbg-pbj-up', '9', '♦', true),    // dealer up
      makeCard('dbg-pbj-p1', 'A', '♠', true)     // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealTableDealerAce: () => {
    const state = get();
    const { settings, tableConfig } = state;
    // Dealing order (pop last -> first dealt):
    // seat0 c1, seat1 c1, user c1, dealer up, seat0 c2, seat1 c2, user c2, dealer hole
    const stackCards = [
      makeCard('dbg-tda-hole', '9', '♦', false),   // dealer hole (last dealt)
      makeCard('dbg-tda-u2', '2', '♣', true),      // user second
      makeCard('dbg-tda-s1-2', '6', '♥', true),    // seat1 second
      makeCard('dbg-tda-s0-2', '7', '♦', true),    // seat0 second
      makeCard('dbg-tda-up', 'A', '♣', true),      // dealer up (ace)
      makeCard('dbg-tda-u1', '9', '♥', true),      // user first
      makeCard('dbg-tda-s1-1', '8', '♠', true),    // seat1 first
      makeCard('dbg-tda-s0-1', '9', '♣', true)     // seat0 first (first dealt)
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    const newSeats = createSeats(tableConfig).map(seat => ({
      ...seat,
      isActive: seat.isActive,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackedDeck,
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTablePlayerBJ: () => {
    const state = get();
    const { settings, tableConfig } = state;
    // User (seat3) gets blackjack; dealer non-BJ
    const seat0First = makeCard('dbg-tdb-s0-1', '5', '♠', true);
    const seat1First = makeCard('dbg-tdb-s1-1', '8', '♣', true);
    const userFirst = makeCard('dbg-tdb-u1', 'A', '♠', true);
    const dealerUp = makeCard('dbg-tdb-up', '9', '♠', true);
    const seat0Second = makeCard('dbg-tdb-s0-2', '6', '♦', true);
    const seat1Second = makeCard('dbg-tdb-s1-2', '9', '♥', true);
    const userSecond = makeCard('dbg-tdb-u2', 'K', '♦', true); // completes BJ
    const dealerHole = makeCard('dbg-tdb-hole', '7', '♦', false); // no dealer BJ
    const stackCards = [
      dealerHole,
      userSecond,
      seat1Second,
      seat0Second,
      dealerUp,
      userFirst,
      seat1First,
      seat0First
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackedDeck,
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealDealerBJTraining: () => {
    const state = get();
    const { settings } = state;
    const stackCards = [
      makeCard('dbg-dbj-hole', 'K', '♦', true),   // dealer hole
      makeCard('dbg-dbj-p2', '9', '♥', true),
      makeCard('dbg-dbj-up', 'A', '♠', true),
      makeCard('dbg-dbj-p1', '10', '♣', true)
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);

    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealDealerTenUpBJTraining: () => {
    const state = get();
    const { settings } = state;
    // Dealer upcard is 10-value, hole is Ace -> dealer blackjack
    const stackCards = [
      makeCard('dbg-dt-hole', 'A', '♠', false),  // dealer hole
      makeCard('dbg-dt-p2', '9', '♣', true),     // player second
      makeCard('dbg-dt-up', 'K', '♦', true),     // dealer up (10 value)
      makeCard('dbg-dt-p1', '8', '♥', true)      // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);

    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });

    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealDealerTenUpNonBJTraining: () => {
    const state = get();
    const { settings } = state;
    // Dealer upcard 10, hole 9 (no BJ) to confirm no early reveal
    const stackCards = [
      makeCard('dbg-tnb-hole', '9', '♠', false),  // dealer hole
      makeCard('dbg-tnb-p2', '7', '♦', true),     // player second
      makeCard('dbg-tnb-up', 'K', '♣', true),     // dealer up (10 value)
      makeCard('dbg-tnb-p1', '9', '♥', true)      // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealPlayerBustTraining: () => {
    const state = get();
    const { settings } = state;
    // Player 10/6 vs dealer 5; next hit card is 10 to force bust
    const stackCards = [
      makeCard('dbg-bust-hit', '10', '♠', true),   // next card to draw on hit
      makeCard('dbg-bust-hole', '9', '♦', false),  // dealer hole
      makeCard('dbg-bust-p2', '6', '♥', true),     // player second
      makeCard('dbg-bust-up', '5', '♣', true),     // dealer up
      makeCard('dbg-bust-p1', '10', '♦', true)     // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealSplitPairTraining: () => {
    const state = get();
    const { settings } = state;
    // Player gets pair of 8s vs dealer 6 to test split
    const stackCards = [
      makeCard('dbg-split-hit2', '3', '♠', true),   // follow-up draws
      makeCard('dbg-split-hit1', '2', '♣', true),
      makeCard('dbg-split-hole', '9', '♣', false),  // dealer hole
      makeCard('dbg-split-p2', '8', '♥', true),     // player second (pair)
      makeCard('dbg-split-up', '6', '♦', true),     // dealer up
      makeCard('dbg-split-p1', '8', '♠', true)      // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false,
      lastBaseBet: settings.minBet,
      playerHands: [{ cards: [], bet: settings.minBet }],
      playerHand: { cards: [], bet: settings.minBet }
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealDoubleThenBustTraining: () => {
    const state = get();
    const { settings } = state;
    // Player 11 vs dealer 6; next draw is 10 to bust after double
    const stackCards = [
      makeCard('dbg-dbl-hit', '10', '♥', true),    // draw on double
      makeCard('dbg-dbl-hole', '9', '♠', false),   // dealer hole
      makeCard('dbg-dbl-p2', '5', '♦', true),      // player second (total 11)
      makeCard('dbg-dbl-up', '6', '♣', true),      // dealer up
      makeCard('dbg-dbl-p1', '6', '♦', true)       // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealPushTraining: () => {
    const state = get();
    const { settings } = state;
    // Push: player 20 vs dealer 20 (dealer 10 up, Q hole)
    const stackCards = [
      makeCard('dbg-push-hole', 'Q', '♣', false),  // dealer hole -> 20
      makeCard('dbg-push-p2', '10', '♦', true),    // player second
      makeCard('dbg-push-up', '10', '♠', true),    // dealer up
      makeCard('dbg-push-p1', 'K', '♥', true)      // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    set({
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });
    setTimeout(() => get().dealNewHand(), 0);
  },

  debugDealEarlySurrenderTraining: () => {
    const state = get();
    const { settings } = state;
    const prevSettings = settings;
    // Early surrender test: dealer Ace up, non-BJ hole, player 16
    const stackCards = [
      makeCard('dbg-es-hole', '9', '♦', false),   // dealer hole (no BJ)
      makeCard('dbg-es-p2', '10', '♣', true),     // player second
      makeCard('dbg-es-up', 'A', '♠', true),      // dealer up (insurance prompt)
      makeCard('dbg-es-p1', '6', '♥', true)       // player first -> 16
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);

    suppressSettingsPersist = true;
    set({
      settings: { ...state.settings, earlySurrenderAllowed: true, surrenderAllowed: true },
      debugSettingsRestore: prevSettings,
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });

    setTimeout(() => {
      get().dealNewHand();
    }, 0);
  },

  debugDealLateSurrenderTraining: () => {
    const state = get();
    const { settings } = state;
    const prevSettings = settings;
    // Late surrender test: dealer 10 up, non-BJ hole, player 16
    const stackCards = [
      makeCard('dbg-ls-hole', '9', '♠', false),   // dealer hole (no BJ)
      makeCard('dbg-ls-p2', '10', '♦', true),     // player second
      makeCard('dbg-ls-up', 'K', '♣', true),      // dealer up (10)
      makeCard('dbg-ls-p1', '6', '♣', true)       // player first -> 16
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);

    suppressSettingsPersist = true;
    set({
      settings: { ...state.settings, surrenderAllowed: true, earlySurrenderAllowed: false },
      debugSettingsRestore: prevSettings,
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false
    });

    setTimeout(() => {
      get().dealNewHand();
    }, 0);
  },

  debugDealSplitAces21Training: () => {
    const state = get();
    const { settings } = state;
    const prevSettings = settings;
    // Splitting aces: A/A vs dealer 6, next draws 10 and 9 (first hand 21 from split aces, second 20)
    const stackCards = [
      makeCard('dbg-sa-hit2', '9', '♣', true),    // for second ace hand
      makeCard('dbg-sa-hit1', '10', '♦', true),   // for first ace hand -> 21 (non-BJ)
      makeCard('dbg-sa-hole', '9', '♠', false),   // dealer hole (no BJ)
      makeCard('dbg-sa-p2', 'A', '♦', true),      // player second (pair)
      makeCard('dbg-sa-up', '6', '♣', true),      // dealer up
      makeCard('dbg-sa-p1', 'A', '♠', true)       // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    suppressSettingsPersist = true;
    set({
      settings: { ...state.settings, earlySurrenderAllowed: false, surrenderAllowed: true },
      debugSettingsRestore: prevSettings,
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false,
      playerHands: [{ cards: [], bet: settings.minBet }],
      playerHand: { cards: [], bet: settings.minBet }
    });
    setTimeout(() => {
      get().dealNewHand();
    }, 0);
  },

  debugDealLateSurrenderDealerBJTraining: () => {
    const state = get();
    const { settings } = state;
    const prevSettings = settings;
    // Dealer Ace up with 10 hole (BJ); player 16; late surrender should be blocked
    const stackCards = [
      makeCard('dbg-lsbj-hole', '10', '♦', false), // dealer hole (BJ)
      makeCard('dbg-lsbj-p2', '10', '♣', true),    // player second -> 16
      makeCard('dbg-lsbj-up', 'A', '♠', true),     // dealer up
      makeCard('dbg-lsbj-p1', '6', '♥', true)      // player first
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    suppressSettingsPersist = true;
    set({
      settings: { ...state.settings, earlySurrenderAllowed: false, surrenderAllowed: true },
      debugSettingsRestore: prevSettings,
      deck: stackedDeck,
      dealtCards: [],
      runningCount: 0,
      trueCount: 0,
      insuranceOffered: false,
      insuranceResult: null,
      insuranceBet: 0,
      isPlaying: false,
      isDealing: false,
      playerTurnComplete: false,
      roundComplete: false,
      playerHands: [{ cards: [], bet: settings.minBet }],
      playerHand: { cards: [], bet: settings.minBet }
    });
    setTimeout(() => {
      get().dealNewHand();
    }, 0);
  },

  debugDealTableDealerBJ: () => {
    const state = get();
    const { settings, tableConfig } = state;
    // Dealer blackjack (Ace up, ten-value hole). Seat order: AI0, AI1, User.
    const stackCards = [
      makeCard('dbg-tdbj-hole', '10', '♥', false), // dealer hole (BJ, last dealt)
      makeCard('dbg-tdbj-u2', '6', '♣', true),     // user second -> 16
      makeCard('dbg-tdbj-s1-2', '5', '♦', true),   // seat1 second
      makeCard('dbg-tdbj-s0-2', 'A', '♣', true),   // seat0 second (soft 17)
      makeCard('dbg-tdbj-up', 'A', '♥', true),     // dealer up (Ace)
      makeCard('dbg-tdbj-u1', '10', '♦', true),    // user first
      makeCard('dbg-tdbj-s1-1', '7', '♠', true),   // seat1 first
      makeCard('dbg-tdbj-s0-1', '6', '♦', true)    // seat0 first (first dealt)
    ];
    const stackedDeck = stackDeck(createShoe(settings.numDecks), stackCards);
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackedDeck,
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableDealerTenUpBJ: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-tdtubj-s0-1', '6', '♠', true),
      seat1First: makeCard('dbg-tdtubj-s1-1', '7', '♦', true),
      userFirst: makeCard('dbg-tdtubj-u1', '8', '♣', true),
      dealerUp: makeCard('dbg-tdtubj-up', 'K', '♣', true), // 10-value up
      seat0Second: makeCard('dbg-tdtubj-s0-2', '5', '♥', true),
      seat1Second: makeCard('dbg-tdtubj-s1-2', '4', '♣', true),
      userSecond: makeCard('dbg-tdtubj-u2', '9', '♦', true),
      dealerHole: makeCard('dbg-tdtubj-hole', 'A', '♠', false) // dealer BJ
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableDealerTenUpNonBJ: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-tdtunb-s0-1', '6', '♣', true),
      seat1First: makeCard('dbg-tdtunb-s1-1', '7', '♥', true),
      userFirst: makeCard('dbg-tdtunb-u1', '9', '♣', true),
      dealerUp: makeCard('dbg-tdtunb-up', 'Q', '♦', true), // 10-value up
      seat0Second: makeCard('dbg-tdtunb-s0-2', '5', '♠', true),
      seat1Second: makeCard('dbg-tdtunb-s1-2', '4', '♠', true),
      userSecond: makeCard('dbg-tdtunb-u2', '7', '♦', true),
      dealerHole: makeCard('dbg-tdtunb-hole', '9', '♠', false) // no BJ
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTablePlayerBust: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const hitCard = makeCard('dbg-td-bust-hit', '10', '♠', true);
    const stackCards = buildTableStack(
      {
        seat0First: makeCard('dbg-td-bust-s0-1', '6', '♠', true),
        seat1First: makeCard('dbg-td-bust-s1-1', '7', '♦', true),
        userFirst: makeCard('dbg-td-bust-u1', '10', '♦', true),
        dealerUp: makeCard('dbg-td-bust-up', '5', '♣', true),
        seat0Second: makeCard('dbg-td-bust-s0-2', '4', '♠', true),
        seat1Second: makeCard('dbg-td-bust-s1-2', '8', '♣', true),
        userSecond: makeCard('dbg-td-bust-u2', '6', '♥', true), // user 16 vs 5
        dealerHole: makeCard('dbg-td-bust-hole', '9', '♦', false)
      },
      [hitCard]
    );
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableSplitPair: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const splitHit1 = makeCard('dbg-td-split-hit1', '2', '♣', true);
    const splitHit2 = makeCard('dbg-td-split-hit2', '3', '♠', true);
    const stackCards = buildTableStack(
      {
        seat0First: makeCard('dbg-td-split-s0-1', '4', '♠', true),
        seat1First: makeCard('dbg-td-split-s1-1', '7', '♦', true),
        userFirst: makeCard('dbg-td-split-u1', '8', '♠', true),
        dealerUp: makeCard('dbg-td-split-up', '6', '♦', true),
        seat0Second: makeCard('dbg-td-split-s0-2', '5', '♥', true),
        seat1Second: makeCard('dbg-td-split-s1-2', '9', '♣', true),
        userSecond: makeCard('dbg-td-split-u2', '8', '♥', true), // pair of 8s
        dealerHole: makeCard('dbg-td-split-hole', '9', '♠', false)
      },
      [splitHit2, splitHit1]
    );
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableDoubleThenBust: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const hitCard = makeCard('dbg-td-dbl-hit', '10', '♥', true);       // user draw after double (busts 12 -> 22)
    const dealerDraw = makeCard('dbg-td-dbl-dealer-draw', '5', '♦', true); // dealer draws to 20 after player busts
    const stackCards = buildTableStack(
      {
        seat0First: makeCard('dbg-td-dbl-s0-1', '4', '♣', true),
        seat1First: makeCard('dbg-td-dbl-s1-1', '7', '♣', true),
        userFirst: makeCard('dbg-td-dbl-u1', '7', '♦', true),
        dealerUp: makeCard('dbg-td-dbl-up', '6', '♣', true),
        seat0Second: makeCard('dbg-td-dbl-s0-2', '5', '♣', true),
        seat1Second: makeCard('dbg-td-dbl-s1-2', '9', '♦', true),
        userSecond: makeCard('dbg-td-dbl-u2', '5', '♥', true), // user 12 (bad double -> bust)
        dealerHole: makeCard('dbg-td-dbl-hole', '9', '♠', false)
      },
      [hitCard, dealerDraw] // hit card for user, then dealer draw
    );
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTablePush: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-td-push-s0-1', '6', '♥', true),
      seat1First: makeCard('dbg-td-push-s1-1', '8', '♠', true),
      userFirst: makeCard('dbg-td-push-u1', 'K', '♥', true),
      dealerUp: makeCard('dbg-td-push-up', '10', '♠', true),
      seat0Second: makeCard('dbg-td-push-s0-2', '4', '♦', true),
      seat1Second: makeCard('dbg-td-push-s1-2', '7', '♣', true),
      userSecond: makeCard('dbg-td-push-u2', '10', '♦', true), // user 20
      dealerHole: makeCard('dbg-td-push-hole', 'Q', '♣', false) // dealer 20
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    set({
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableEarlySurrender: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const prevSettings = settings;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-td-es-s0-1', '6', '♠', true),
      seat1First: makeCard('dbg-td-es-s1-1', '8', '♥', true),
      userFirst: makeCard('dbg-td-es-u1', '6', '♥', true),
      dealerUp: makeCard('dbg-td-es-up', 'A', '♠', true),
      seat0Second: makeCard('dbg-td-es-s0-2', '4', '♣', true),
      seat1Second: makeCard('dbg-td-es-s1-2', '7', '♠', true),
      userSecond: makeCard('dbg-td-es-u2', '10', '♣', true), // user 16 vs Ace
      dealerHole: makeCard('dbg-td-es-hole', '9', '♦', false) // no BJ
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    suppressSettingsPersist = true;
    set({
      settings: { ...settings, earlySurrenderAllowed: true, surrenderAllowed: true },
      debugSettingsRestore: prevSettings,
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableLateSurrender: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const prevSettings = settings;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-td-ls-s0-1', '6', '♣', true),
      seat1First: makeCard('dbg-td-ls-s1-1', '7', '♣', true),
      userFirst: makeCard('dbg-td-ls-u1', '6', '♣', true),
      dealerUp: makeCard('dbg-td-ls-up', 'K', '♣', true), // 10 up
      seat0Second: makeCard('dbg-td-ls-s0-2', '5', '♦', true),
      seat1Second: makeCard('dbg-td-ls-s1-2', '8', '♦', true),
      userSecond: makeCard('dbg-td-ls-u2', '10', '♦', true), // user 16
      dealerHole: makeCard('dbg-td-ls-hole', '9', '♠', false) // no BJ
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    suppressSettingsPersist = true;
    set({
      settings: { ...settings, surrenderAllowed: true, earlySurrenderAllowed: false },
      debugSettingsRestore: prevSettings,
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableSplitAces21: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const prevSettings = settings;
    const hitFirst = makeCard('dbg-td-sa-hit1', '10', '♦', true);
    const hitSecond = makeCard('dbg-td-sa-hit2', '9', '♣', true);
    const stackCards = buildTableStack(
      {
        seat0First: makeCard('dbg-td-sa-s0-1', '6', '♥', true),
        seat1First: makeCard('dbg-td-sa-s1-1', '7', '♠', true),
        userFirst: makeCard('dbg-td-sa-u1', 'A', '♠', true),
        dealerUp: makeCard('dbg-td-sa-up', '6', '♣', true),
        seat0Second: makeCard('dbg-td-sa-s0-2', '5', '♠', true),
        seat1Second: makeCard('dbg-td-sa-s1-2', '8', '♦', true),
        userSecond: makeCard('dbg-td-sa-u2', 'A', '♦', true), // split aces
        dealerHole: makeCard('dbg-td-sa-hole', '9', '♠', false)
      },
      [hitSecond, hitFirst]
    );
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    suppressSettingsPersist = true;
    set({
      settings: { ...settings, earlySurrenderAllowed: false, surrenderAllowed: true },
      debugSettingsRestore: prevSettings,
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  debugDealTableLateSurrenderDealerBJ: () => {
    const state = get();
    const { settings, tableConfig } = state;
    const prevSettings = settings;
    const stackCards = buildTableStack({
      seat0First: makeCard('dbg-td-lsbj-s0-1', '6', '♦', true),
      seat1First: makeCard('dbg-td-lsbj-s1-1', '7', '♦', true),
      userFirst: makeCard('dbg-td-lsbj-u1', '6', '♥', true),
      dealerUp: makeCard('dbg-td-lsbj-up', 'A', '♠', true),
      seat0Second: makeCard('dbg-td-lsbj-s0-2', '5', '♣', true),
      seat1Second: makeCard('dbg-td-lsbj-s1-2', '9', '♣', true),
      userSecond: makeCard('dbg-td-lsbj-u2', '10', '♣', true), // user 16
      dealerHole: makeCard('dbg-td-lsbj-hole', '10', '♦', false) // dealer BJ
    });
    const baseSeats = createSeats(tableConfig);
    const newSeats = baseSeats.map(seat => ({
      ...seat,
      hands: [{ cards: [], bet: seat.isActive ? settings.minBet : 0, result: undefined }]
    }));
    suppressSettingsPersist = true;
    set({
      settings: { ...settings, surrenderAllowed: true, earlySurrenderAllowed: false },
      debugSettingsRestore: prevSettings,
      tableSimDeck: {
        deck: stackDeck(createShoe(settings.numDecks), stackCards),
        deckSeed: null, // Debug stacked deck - no seed tracking
        dealtCards: [],
        runningCount: 0,
        trueCount: 0,
        dealerHand: { cards: [], bet: 0 }
      },
      seats: newSeats,
      tableState: {
        dealingPhase: 'idle',
        currentSeatIndex: 0,
        currentHandIndex: 0,
        isAutoPlaying: false,
        lastDecisionAnalysis: null,
        insuranceOffered: false,
        insuranceTaken: false
      }
    });
    setTimeout(() => get().startTableRound(), 0);
  },

  toggleSeat: (seatNumber: number) => {
    const { seats, tableState } = get();

    // Only allow toggling when idle
    if (tableState.dealingPhase !== 'idle') return;

    const seat = seats[seatNumber];
    if (!seat || seat.playerType === 'user') return; // Can't toggle user seat

    const newSeats = [...seats];

    if (seat.playerType === 'empty') {
      // Make it an AI seat
      newSeats[seatNumber] = {
        ...seat,
        playerType: 'ai',
        isActive: true,
        hands: [{ cards: [], bet: 0 }]
      };
    } else if (seat.playerType === 'ai') {
      // Make it empty
      newSeats[seatNumber] = {
        ...seat,
        playerType: 'empty',
        isActive: false,
        hands: [{ cards: [], bet: 0 }]
      };
    }

    set({ seats: newSeats });
  },

  triggerCountCheck: () => {
    const { tableSimDeck, gameMode, runningCount } = get();
    // Use table sim's count for table sim mode, otherwise use training count
    const correctCount = gameMode === 'table-simulator' ? tableSimDeck.runningCount : runningCount;
    set({
      countCheck: {
        isActive: true,
        correctAnswer: correctCount,
        userAnswer: null,
        result: null
      }
    });
  },

  submitCountCheck: (answer: number | null) => {
    const { countCheck, statistics } = get();
    if (!countCheck.isActive) return;

    const isCorrect = answer === countCheck.correctAnswer;
    const result = answer === null ? 'skipped' : (isCorrect ? 'correct' : 'incorrect');

    // Play feedback sound
    if (result === 'correct') {
      sounds.playCorrect();
    } else if (result === 'incorrect') {
      sounds.playIncorrect();
    }

    // Update statistics
    const newStats = { ...statistics };
    if (result === 'correct') {
      newStats.consecutiveCorrect++;
      newStats.bestStreak = Math.max(newStats.bestStreak, newStats.consecutiveCorrect);
    } else {
      newStats.consecutiveCorrect = 0;
      if (result === 'incorrect') {
        newStats.countErrors++;
      }
    }

    set({
      countCheck: {
        ...countCheck,
        userAnswer: answer,
        result
      },
      statistics: newStats
    });

    // Auto-dismiss after a delay
    setTimeout(() => {
      get().dismissCountCheck();
    }, 2000);
  },

  dismissCountCheck: () => {
    set({
      countCheck: DEFAULT_COUNT_CHECK
    });
  },

  clearHandHistory: () => {
    set({ handHistory: [] });
    // History is persisted to localStorage via subscriber
  },

  dismissTutorial: () => {
    set({ showTutorial: false });
    // Tutorial state is persisted to localStorage via subscriber
    // Also update the instant-load cache
    try {
      localStorage.setItem(TUTORIAL_CACHE_KEY, 'true');
    } catch {
      // Ignore localStorage errors
    }
  },

  updateScenarioStats: (category: string, difficulty: string, isCorrect: boolean) => {
    const state = get();
    const newStats = { ...state.statistics };

    // Update overall scenario stats
    newStats.scenariosAttempted++;
    if (isCorrect) {
      newStats.scenariosCorrect++;
    }

    // Update category stats
    if (newStats.scenariosByCategory[category]) {
      newStats.scenariosByCategory[category].attempted++;
      if (isCorrect) {
        newStats.scenariosByCategory[category].correct++;
      }
    }

    // Update difficulty stats
    if (newStats.scenariosByDifficulty[difficulty]) {
      newStats.scenariosByDifficulty[difficulty].attempted++;
      if (isCorrect) {
        newStats.scenariosByDifficulty[difficulty].correct++;
      }
    }

    set({ statistics: newStats });
  },

  checkAchievements: () => {
    const state = get();
    const { statistics, achievements, speedDrill, settings } = state;
    const definitions = (state.achievementDefinitions?.length ? state.achievementDefinitions : combineAchievementDefinitions(settings));
    const extras = { speedDrillHighScore: speedDrill.highScore };

    let newAchievementUnlocked = false;
    const newStats = { ...statistics };
    const updatedAchievements = [...achievements];

    for (const achievement of definitions) {
      const progress = getAchievementProgressByDefinition(achievement, statistics, updatedAchievements, extras);
      const existing = updatedAchievements.find(a => a.achievementId === achievement.id);

      if (!existing && progress.unlocked) {
        updatedAchievements.push({
          achievementId: achievement.id,
          currentValue: progress.current,
          unlocked: true,
          unlockedAt: Date.now()
        });
        newStats.totalXP += computeAchievementReward(achievement);
        newStats.level = calculateLevel(newStats.totalXP, settings.leveling);
        newAchievementUnlocked = true;
      } else if (existing && !existing.unlocked && progress.unlocked) {
        existing.unlocked = true;
        existing.unlockedAt = Date.now();
        newStats.totalXP += computeAchievementReward(achievement);
        newStats.level = calculateLevel(newStats.totalXP, settings.leveling);
        newAchievementUnlocked = true;
      }
    }

    if (newAchievementUnlocked) {
      sounds.playAchievement();
      set({ achievements: updatedAchievements, achievementDefinitions: definitions, statistics: newStats });
      persistStats(newStats);
      queueAchievementsSave(updatedAchievements);
    } else if (state.achievementDefinitions !== definitions) {
      set({ achievementDefinitions: definitions });
    }
  }
}));

// Subscribe to state changes to persist data
// Firebase: settings, stats, achievements, highScores (cross-device sync)
// localStorage: session state, hand history (device-specific)
useGameStore.subscribe((state, prevState) => {
  // ===========================================
  // FIREBASE PERSISTENCE (cross-device sync)
  // ===========================================
  
  // Persist statistics if changed (Firebase)
  if (state.statistics !== prevState.statistics) {
    persistStats(state.statistics);
  }

  // Persist settings if changed (Firebase)
  if (state.settings !== prevState.settings) {
    if (!suppressSettingsPersist) {
      queueSettingsSave(state.settings);
    } else {
      suppressSettingsPersist = false;
    }
  }

  // Persist achievements changes (Firebase)
  if (state.achievements !== prevState.achievements) {
    queueAchievementsSave(state.achievements);
  }

  // Persist high score changes (Firebase - cross-device sync)
  if (state.speedDrill.highScore !== prevState.speedDrill.highScore) {
    queueHighScoresSave(state.speedDrill.highScore);
  }

  // ===========================================
  // LOCALSTORAGE PERSISTENCE (device-specific)
  // ===========================================
  
  // Check if any session-related state changed
  const sessionChanged = 
    state.bankroll !== prevState.bankroll ||
    state.gameMode !== prevState.gameMode ||
    state.showTutorial !== prevState.showTutorial ||
    state.speedDrill.highScore !== prevState.speedDrill.highScore ||
    state.dealtCards.length !== prevState.dealtCards.length ||
    state.deckSeed !== prevState.deckSeed ||
    state.runningCount !== prevState.runningCount ||
    state.tableSimDeck.dealtCards.length !== prevState.tableSimDeck.dealtCards.length ||
    state.tableSimDeck.deckSeed !== prevState.tableSimDeck.deckSeed ||
    state.tableSimDeck.runningCount !== prevState.tableSimDeck.runningCount;

  if (sessionChanged) {
    saveLocalSession();
  }

  // Also update tutorial cache for instant load
  if (state.showTutorial !== prevState.showTutorial) {
    try {
      localStorage.setItem(TUTORIAL_CACHE_KEY, state.showTutorial ? 'false' : 'true');
    } catch {
      // Ignore localStorage errors
    }
  }

  // Persist hand history to localStorage if changed
  if (state.handHistory !== prevState.handHistory) {
    saveLocalHistory(state.handHistory);
  }
});
